import { SourceCurrentApplication } from "@/components/benefit/source-current-application"
import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, Fragment } from "react"
import { useBenefitEditRegistration } from "@/components/benefit/benefit-edit-scope"
import { useParams, useNavigate } from "react-router-dom"
import {
  IconArrowLeft,
  IconLoader2,
  IconUser,
  IconUsers,
  IconHome,
  IconMail,
  IconFlame,
  IconCurrencyDollar,
  IconClipboardCheck,
  IconBuildingCommunity,
  IconPhone,
  IconReceipt,
  IconChevronDown,
  IconChevronRight,
  IconDeviceFloppy,
  IconCirclePlus,
  IconAlertTriangle,
  IconCheck,
  IconArrowsExchange,
  IconScale,
  IconExternalLink,
  IconPlayerPlay,
  IconRefresh,
} from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { ConfidenceBadge } from "@/components/confidence-badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { api } from "@/lib/api"
import { PersonIdentityFields } from "@/components/people/person-identity-form"
import { formatPersonName } from "@/lib/person-identity"
import { cn } from "@/lib/utils"
import { useAppSelector } from "@/store"
import { canManagePseDistribution, canUpdateIndividualStaffProgress } from "@/app/pse-online/permissions"
import { applicableProgramYear } from "@/lib/program-year"
import { currentBenefitData, formatBenefitMoney, getBenefitAccountNumbers } from "@/lib/pse-match-display"
import { priorHelpEligibility, priorHouseholdCount, priorMailingAddress, priorPseEnergyType, type PriorHouseholdComparison } from "@/lib/pse-household-comparison"
import { durablePseBenefitId, pseBenefitResumePath } from "@/lib/pse-create-handoff"
import { relationshipLabels, selectOptions } from "@/lib/benefit-enums"
import {
  householdMemberSlot,
  fillUnansweredRelationships,
  reconcileApplicantSelection,
  reviewedApplicantSlots,
  type HouseholdReviewDocument,
} from "../householdReviewUi"
import { acceptMatchRefresh, reconcileRefreshedMatches } from "./matchRefresh"
import {
  fullName as snapshotFullName,
  incompleteNotice,
  snapshotDestination,
  type CompanionDispatchResult,
  type CompanionStatusDto,
  type SnapshotMemberDecision,
  type SnapshotReview,
} from "../snapshot-import/snapshot"

// ── Type Definitions ──────────────────────────────────────────────────

interface PersonMatch {
  personId: string
  confidence: number
  matchType: string
  matchedFields: string[]
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  ssnLast4?: string
  previousApplications?: Array<{
    benefitId: string
    fileNumber: number
    programYear: number
    address: string
    pseApplicationType?: number | null
  }>
}

interface PersonCsvData {
  firstName: string
  lastName: string
  middleInitial?: string
  ssnLast4?: string | null
  dateOfBirth?: string | null
  accountNumber?: string
  applicantType?: string | null
  emailAddress?: string | null
  phoneNumber?: string | null
}

interface NewPersonData {
  firstName: string
  lastName: string
  middleName: string
  /** Full SSN entered by an authorized staff member; never inferred from imported last-four data. */
  socialSecurityNumber: string
  dateOfBirth: string
  gender: number
}

interface PersonSection {
  csvData: PersonCsvData
  matches: PersonMatch[]
  decision: "use_existing" | "create_new" | null
  selectedPersonId: string | null
  newPersonData: NewPersonData | null
  snapshotMember?: { firstName?: string | null; lastName?: string | null; dateOfBirth?: string | null }
}

interface HouseholdMemberSection {
  index: number
  ageRange: string
  matches: PersonMatch[]
  decision: "use_existing" | "create_new" | "exclude" | null
  selectedPersonId: string | null
  newPersonData: NewPersonData | null
  exclusionReason?: string | null
  snapshotMember?: {
    name?: string | null
    firstName?: string | null
    lastName?: string | null
    dateOfBirth?: string | null
    ssnLast4?: string | null
    isDisabled?: boolean
  } | null
}

interface PreviousApplication {
  benefitId: string
  fileNumber: number
  programYear: number
  applicantName?: string
  address: string
  accountNumber?: string
  confidence: number
  matchSource: string
  pseApplicationType?: number | null
  householdComposition?: {
    csvMemberCount: number
    existingMemberCount: number
    members: Array<{
      personId: string
      firstName: string
      lastName: string
      dateOfBirth?: string
      relationship?: string
    }>
  }
}

interface MatchDocument {
  accountHolder: PersonSection
  accountHolderHouseholdExclusionReason?: string | null
  coCustomer: PersonSection | null
  householdMembers: HouseholdMemberSection[]
  previousApplications: PreviousApplication[]
  overallConfidence: number
  incomeAssignment?: "accountHolder" | "coApplicant" | "split" | "custom" | null
  /** Null/missing preserves the historical account-holder applicant default. */
  applicantSelection?: {
    selectedPersonId: string | null
    selectedPersonSlot?: string | null
    accountHolderRelationship: number | null
    relationships?: Record<string, number>
    invalidated?: boolean
    relationshipReviewRequired?: boolean
  } | null
  snapshotEnrichment?: {
    snapshotId: string
    applicationId: string
    programYear: number
    capturedAt: string
    confirmedAt: string
    confirmedBy: string
    decisions: SnapshotMemberDecision[]
  } | null
}

interface PersonSearchResult {
  matches: Array<{
    id: string
    firstName: string
    lastName: string
    middleName?: string
    dateOfBirth?: string | null
    birthDate?: string | null
    socialSecurityNumber?: string | null
    gender?: number
  }>
  hasMoreMatches?: boolean
}

interface PseOnlinePersonMatchCandidateResponse {
  personId: string
  firstName: string
  lastName: string
  dateOfBirth?: string | null
  ssnLast4?: string | null
  confidence: number
  matchType: string
  matchedFields: string[]
  previousApplications?: PreviousApplication[]
}

interface PseOnlinePersonMatchRefreshResponse {
  isReady: boolean
  message?: string
  candidates: PseOnlinePersonMatchCandidateResponse[]
}

interface MatchDocumentResponse {
  id: string
  csvRecordId: string
  status: number
  matchDocument: string
  assignedStaffId?: string
  notes?: string
  createdBenefitId?: string
  assignmentVersion: number
  createdAt: string
  updatedAt: string
  /** Canonical identifiers are a permission-gated read-only projection, not document JSON. */
  authorizedIdentifiers?: Record<string, string>
  /** Allows an authorized editor to enter a full SSN for a newly-created canonical person. */
  canEnterFullIdentifiers?: boolean
  creationCapability?: PseOnlineCreationCapability
}

interface PseOnlineCreationCapability {
  mode: "V2" | "Compatibility" | "Rejected" | "AlreadyCreated" | string
  canCreate: boolean
  canApprove: boolean
  disabledReason?: string
  createdBenefitId?: string
}

interface PseOnlineDetailAssignmentHistory {
  id: string
  recordId: string
  oldAssignedStaffId: string | null
  newAssignedStaffId: string | null
  actorId: string
  changedAt: string
  previousVersion: number
  newVersion: number
}

interface PseOnlineDetailStaffOption {
  id: string
  displayName: string
  userPrincipalName: string
}

interface PseOnlineDistributionCapability {
  canManageDistribution: boolean
  canViewStaffOptions: boolean
  maxSelection: number
}

interface PseOnlineDetailStaffProgressHistory {
  id: string
  recordId: string
  fromStatus: number | string
  toStatus: number | string
  previousVersion: number
  newVersion: number
  changedAt: string
  changedBy: string
  notes?: string | null
}

interface ValidationResult {
  isValid: boolean
  warnings: Array<{ field: string; message: string; matchedPersonId?: string; confidence?: number }>
  errors: Array<{ field: string; message: string }>
}

interface CreateBenefitResult {
  success: boolean
  benefitId?: string
  reviewQueueItemId?: string
  message: string
}

interface AddressData {
  address: string
  city: string
  state: string
  zipCode: string
}

interface PseOnlineRecordDetail {
  id: string
  uploadId: string
  rowNumber: number
  pseApplicationId: number
  programYear: number
  applicationCreatedDate: string
  pseStatus: string
  pseStatusLabel?: string
  preProductionStatus: number | string
  staffProgressStatus: number | string
  staffProgressVersion: number
  staffProgressChangedAt: string | null
  staffProgressChangedBy: string | null
  assignedStaffId: string | null
  assignedStaffDisplayName: string | null
  assignmentVersion: number
  accountHolderFirstName: string | null
  accountHolderLastName: string | null
  accountNumber: string | null
  serviceAddress: string | null
  errorMessage: string | null
  processedAt: string | null
  applicationData: {
    pseApplicationNumber: number
    programYear: number
    applicationCreatedDate: string
    lastUpdateDate: string
    certificationDate: string | null
    status: string
    agencyName: string
    agencyId: number
    accountHolder: {
      firstName: string
      middleInitial: string
      lastName: string
      ssnLast4: string | null
      dateOfBirth: string | null
      accountNumber: string
      accountBalance: number
      is18OrEmancipated: boolean
      isAccountHolder: boolean
      applicantType: string | null
      stillLivingAtResidence: boolean
    }
    coCustomer: {
      firstName: string
      lastName: string
      middleInitial: string
      ssnLast4: string | null
      dateOfBirth: string | null
      emailAddress: string | null
      phoneNumber: string | null
    } | null
    serviceAddress: AddressData
    mailingAddress: AddressData
    household: {
      householdMembersCount: number
      monthlyIncome: number
      adjustedGrossIncome: number
      numberOfPersonsAge0to2: number
      numberOfPersonsAge3to5: number
      numberOfPersonsAge6to17: number
      numberOfPersonsAge18to59: number
      numberOfPersonsAge60Plus: number
      numberOfPersonsDisabled: number
      receivingFoodStamps: boolean
      hasMilitaryMember: boolean
      receivedWeatherization: boolean
    }
    incomeSources: Record<string, boolean>
    energyInfo: {
      energyType: string
      annualElectricAmount: number
      annualGasAmount: number
      [key: string]: unknown
    }
    payment: {
      totalPaidAmount: number
      vendorPayments: Array<Record<string, unknown>>
      [key: string]: unknown
    }
    eligibility: {
      isPseHelpEligible: boolean
      eligibilityAmount: number
      eligibilityCategory: string | null
      [key: string]: unknown
    }
    housing: {
      housingStatus: string | null
      housingType: string | null
    }
    contact: {
      emailAddress: string | null
      phoneNumber: string | null
      [key: string]: unknown
    }
  } | null
}

// ── Status Labels ─────────────────────────────────────────────────────

const designationStatusLabels: Record<number, string> = {
  0: "Not Started",
  1: "In Progress",
  2: "Ready for Creation",
  3: "Needs Review",
  4: "Rejected",
  5: "Approved",
  6: "Completed",
}

const staffProgressStatusLabels: Record<number, string> = {
  0: "Not Started",
  1: "In Progress",
  2: "Processed",
}

function staffProgressStatusValue(status: number | string): number {
  if (typeof status === "number") return status
  const numeric = Number(status)
  if (Number.isInteger(numeric) && numeric >= 0) return numeric
  return ({ notstarted: 0, inprogress: 1, processed: 2 } as Record<string, number>)[status.toLowerCase()] ?? -1
}

function StaffProgressBadge({ status }: { status: number | string }) {
  const value = staffProgressStatusValue(status)
  const label = staffProgressStatusLabels[value] ?? `Unknown (${status})`
  if (value === 1) return <Badge variant="secondary">{label}</Badge>
  if (value === 2) return <Badge className="bg-green-600 text-white">{label}</Badge>
  return <Badge variant="outline">{label}</Badge>
}

// ── Helper Components ─────────────────────────────────────────────────

function DesignationStatusBadge({ status }: { status: number }) {
  const label = designationStatusLabels[status] ?? `Unknown (${status})`
  switch (status) {
    case 0: return <Badge variant="outline">{label}</Badge>
    case 1: return <Badge variant="secondary">{label}</Badge>
    case 2: return <Badge className="bg-blue-600 text-white">{label}</Badge>
    case 3: return <Badge className="bg-yellow-500 text-black">{label}</Badge>
    case 4: return <Badge variant="destructive">{label}</Badge>
    case 5: return <Badge className="bg-green-600 text-white">{label}</Badge>
    case 6: return <Badge variant="default">{label}</Badge>
    default: return <Badge variant="outline">{label}</Badge>
  }
}

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === "") return null
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="text-sm font-medium">{String(value)}</span>
    </div>
  )
}

function formatAddress(addr: AddressData | null | undefined): string {
  if (!addr) return "—"
  return [addr.address, addr.city, addr.state, addr.zipCode].filter(Boolean).join(", ")
}

function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "—"
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount)
}

/** Keep the source-system PSE subtype distinct from a generic CMS benefit type. */
function formatPseApplicationType(value: number | null | undefined): string | null {
  switch (value) {
    case 0: return "Regular"
    case 1: return "SDH Demo"
    case 2: return "SDH no Demo"
    case 3: return "Verified Qualified"
    case 4: return "Verified Ineligible"
    default: return null
  }
}

function getFullSsn(value: unknown): string {
  if (typeof value === "string" && value.trim()) return value
  if (value && typeof value === "object") {
    const candidate = value as { value?: unknown; formattedValue?: unknown }
    if (typeof candidate.formattedValue === "string" && candidate.formattedValue.trim()) return candidate.formattedValue
    if (typeof candidate.value === "string" && candidate.value.trim()) return candidate.value
  }
  return "Not available"
}

/** Prettify the raw PSE energy-type codes (e.g. "All_Electric", "Electric___Gas"). */
function formatEnergyType(value: string | null | undefined): string {
  if (!value) return "—"
  switch (value.trim().toLowerCase().replace(/_+/g, "_")) {
    case "all_electric": return "All Electric"
    case "electric_gas": return "Electric & Gas"
    case "gas": return "Gas"
    case "electric": return "Electric"
    default: return value.replace(/_+/g, " ").trim()
  }
}

/** Format a DateOnly string (yyyy-MM-dd) as mm/dd/yyyy without timezone conversion. */
function formatDate(date: string | null | undefined): string {
  if (!date) return "—"
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return date
  const [, year, month, day] = match
  return `${month}/${day}/${year}`
}

function emptyNewPersonData(csvData?: PersonCsvData): NewPersonData {
  return {
    firstName: csvData?.firstName ?? "",
    lastName: csvData?.lastName ?? "",
    middleName: csvData?.middleInitial ?? "",
    // A source age band is not an exact date. Staff must enter DOB explicitly. Imported
    // last-four values are intentionally not copied into this field.
    socialSecurityNumber: "",
    dateOfBirth: csvData?.dateOfBirth ?? "",
    gender: 5,
  }
}

function reviewedHouseholdSummary(document: MatchDocument): { count: number; pending: number } {
  const sections = [...(document.accountHolderHouseholdExclusionReason ? [] : [document.accountHolder]), ...(document.coCustomer ? [document.coCustomer] : [])]
  const members = [...sections, ...document.householdMembers]
  const reviewed = members.filter((member) =>
    member.decision === "use_existing" && Boolean(member.selectedPersonId) ||
    member.decision === "create_new" && Boolean(member.newPersonData),
  )
  const pending = members.filter((member) =>
    member.decision !== "exclude" && !reviewed.includes(member),
  ).length
  return { count: reviewed.length, pending }
}

/** Normalize the wire representation without changing the editor's current object. */
function normalizeMatchDocumentForSave(document: MatchDocument): MatchDocument {
  const selection = document.applicantSelection
  if (!selection) return document

  // An untouched account-holder default is null. Explicit slot-based mappings, including
  // confirmed account-holder relationships, must survive normalization.
  if (!selection.selectedPersonSlot && !selection.selectedPersonId) {
    return { ...document, applicantSelection: null }
  }

  if (!selection.selectedPersonSlot) return document

  const relationships = {
    ...(selection.relationships ?? {}),
    [selection.selectedPersonSlot]: 0,
  }
  return {
    ...document,
    applicantSelection: {
      ...selection,
      accountHolderRelationship: relationships.accountHolder ?? null,
      relationships,
    },
  }
}

// ── New Person Form ───────────────────────────────────────────────────

function NewPersonForm({
  data,
  onChange,
  idPrefix,
  canEnterFullIdentifiers,
}: {
  data: NewPersonData
  onChange: (data: NewPersonData) => void
  idPrefix: string
  canEnterFullIdentifiers: boolean
}) {
  return (
    <div className="mt-3" onClick={(e) => e.stopPropagation()}>
      <PersonIdentityFields
        value={{
          firstName: data.firstName,
          lastName: data.lastName,
          middleName: data.middleName,
          socialSecurityNumber: data.socialSecurityNumber,
          dateOfBirth: data.dateOfBirth,
          gender: String(data.gender),
        }}
        onChange={(next) => onChange({
          ...data,
          firstName: next.firstName,
          lastName: next.lastName,
          middleName: next.middleName,
          socialSecurityNumber: next.socialSecurityNumber,
          dateOfBirth: next.dateOfBirth,
          gender: Number(next.gender),
        })}
        idPrefix={idPrefix}
        canEditFullIdentifiers={canEnterFullIdentifiers}
      />
      <p className="mt-2 text-xs text-muted-foreground">Imported last-four data is never promoted to a full SSN.</p>
    </div>
  )
}

// ── Existing Person Search ────────────────────────────────────────────

function ExistingPersonSearch({
  selectedPersonId,
  excludedPersonIds,
  canSearch,
  idPrefix,
  onSelect,
}: {
  selectedPersonId: string | null
  excludedPersonIds?: string[]
  canSearch: boolean
  idPrefix: string
  onSelect: (match: PersonMatch) => void
}) {
  const [searchText, setSearchText] = useState("")
  const [results, setResults] = useState<PersonMatch[]>([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)

  useEffect(() => {
    if (!canSearch || searchText.trim().length < 3) {
      setResults([])
      setSearchError(null)
      return
    }

    let cancelled = false
    const timer = window.setTimeout(() => {
      setSearching(true)
      setSearchError(null)
      api.get<PersonSearchResult>(`/api/people/search?searchText=${encodeURIComponent(searchText.trim())}`)
        .then((response) => {
          if (cancelled) return
          const excluded = new Set(excludedPersonIds ?? [])
          setResults(response.matches
            .filter((match) => !excluded.has(match.id))
            .map((match): PersonMatch => ({
              personId: match.id,
              confidence: 1,
              matchType: "Existing person search",
              matchedFields: ["manual search"],
              firstName: match.firstName,
              lastName: match.lastName,
              dateOfBirth: match.dateOfBirth ?? match.birthDate ?? undefined,
              ssnLast4: match.socialSecurityNumber?.replace(/\D/g, "").slice(-4) || undefined,
            })))
        })
        .catch((error) => {
          if (!cancelled) setSearchError(error instanceof Error ? error.message : "Person search failed")
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, 250)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [canSearch, excludedPersonIds, searchText])

  if (!canSearch) {
    return <p className="text-muted-foreground text-xs">Existing-person search requires full-identifier permission.</p>
  }

  return (
    <div className="space-y-2 rounded-md border bg-muted/20 p-3">
      <Label htmlFor={`${idPrefix}-existing-search`}>Search existing CMS person</Label>
      <div className="flex items-center gap-2">
        <Input
          id={`${idPrefix}-existing-search`}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Name or SSN"
          autoComplete="off"
        />
        {searching && <IconLoader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />}
      </div>
      {searchError && <p className="text-destructive text-xs">{searchError}</p>}
      {searchText.trim().length > 0 && searchText.trim().length < 3 && (
        <p className="text-muted-foreground text-xs">Enter at least 3 characters to search.</p>
      )}
      {results.length > 0 && (
        <RadioGroup
          value={selectedPersonId ?? ""}
          onValueChange={(value) => {
            const selected = results.find((match) => match.personId === value)
            if (selected) onSelect(selected)
          }}
          className="gap-2"
        >
          {results.map((match) => (
            <label key={match.personId} htmlFor={`${idPrefix}-existing-${match.personId}`} className="flex cursor-pointer items-center gap-2 rounded-md border p-2 hover:bg-accent/50">
              <RadioGroupItem value={match.personId} id={`${idPrefix}-existing-${match.personId}`} />
              <span className="text-sm font-medium">{formatPersonName(match)}</span>
              {match.dateOfBirth && <span className="text-muted-foreground text-xs">DOB: {formatDate(match.dateOfBirth)}</span>}
              {match.ssnLast4 && <span className="text-muted-foreground text-xs">SSN last 4: •••-••-{match.ssnLast4}</span>}
            </label>
          ))}
        </RadioGroup>
      )}
      {!searching && searchText.trim().length >= 3 && results.length === 0 && !searchError && (
        <p className="text-muted-foreground text-xs">No existing CMS people matched this search.</p>
      )}
    </div>
  )
}

// ── Person Match Selection ────────────────────────────────────────────

function PersonMatchSelector({
  title,
  icon,
  csvData,
  matches,
  decision,
  selectedPersonId,
  onDecisionChange,
  onViewBenefit,
  newPersonData,
  onNewPersonDataChange,
  onRefreshMatches,
  authorizedIdentifiers,
  canEnterFullIdentifiers,
  residentialAddress,
  excludedPersonIds,
  onSelectExistingPerson,
}: {
  title: string
  icon: React.ReactNode
  csvData: PersonCsvData
  matches: PersonMatch[]
  decision: "use_existing" | "create_new" | null
  selectedPersonId: string | null
  onDecisionChange: (decision: "use_existing" | "create_new", personId: string | null) => void
  onViewBenefit?: (benefitId: string) => void
  newPersonData?: NewPersonData | null
  onNewPersonDataChange?: (data: NewPersonData) => void
  onRefreshMatches?: (data: NewPersonData) => Promise<PersonMatch[]>
  authorizedIdentifiers?: Record<string, string>
  canEnterFullIdentifiers?: boolean
  residentialAddress?: string | null
  excludedPersonIds?: string[]
  onSelectExistingPerson?: (match: PersonMatch) => void
}) {
  const [refreshingMatches, setRefreshingMatches] = useState(false)
  const [refreshMatchesError, setRefreshMatchesError] = useState<string | null>(null)
  const currentValue = decision === "use_existing" && selectedPersonId
    ? `existing_${selectedPersonId}`
    : decision === "create_new"
      ? "create_new"
      : ""

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* CSV Data Summary */}
        <div className="rounded-md border bg-muted/30 p-3">
          <p className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide">Imported Data</p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
            <DataRow label="Name" value={formatPersonName({ firstName: csvData.firstName, middleName: csvData.middleInitial, lastName: csvData.lastName })} />
            <DataRow label="SSN Last 4" value={csvData.ssnLast4 ? `•••-••-${csvData.ssnLast4}` : null} />
            <DataRow label="DOB" value={formatDate(csvData.dateOfBirth)} />
            {csvData.accountNumber && <DataRow label="Account #" value={csvData.accountNumber} />}
            {residentialAddress && <DataRow label="Residential Address" value={residentialAddress} />}
            {csvData.applicantType && <DataRow label="Type" value={csvData.applicantType} />}
            {csvData.emailAddress && <DataRow label="Email" value={csvData.emailAddress} />}
            {csvData.phoneNumber && <DataRow label="Phone" value={csvData.phoneNumber} />}
          </div>
        </div>

        <Separator />

        {/* Match Selection */}
        <div>
          <p className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wide">Person Match Decision</p>
          {onSelectExistingPerson && (
            <div className="mb-3">
              <ExistingPersonSearch
                selectedPersonId={selectedPersonId}
                excludedPersonIds={excludedPersonIds}
                canSearch={canEnterFullIdentifiers === true}
                idPrefix={`search-${title}`}
                onSelect={onSelectExistingPerson}
              />
            </div>
          )}
          {matches.length === 0 ? (
            <Alert>
              <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                <span>No matches found in the original source snapshot. Validate the entered identity before creating a new person.</span>
                {decision === "create_new" && newPersonData && onRefreshMatches && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={refreshingMatches}
                    onClick={async () => {
                      setRefreshingMatches(true)
                      setRefreshMatchesError(null)
                      try {
                        await onRefreshMatches(newPersonData)
                      } catch (error) {
                        setRefreshMatchesError(error instanceof Error ? error.message : "Unable to refresh person matches.")
                      } finally {
                        setRefreshingMatches(false)
                      }
                    }}
                  >
                    {refreshingMatches ? <IconLoader2 className="mr-1 size-4 animate-spin" /> : <IconRefresh className="mr-1 size-4" />}
                    Check current CMS people
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          ) : null}
          {refreshMatchesError && <p className="text-sm text-destructive">{refreshMatchesError}</p>}
          {matches.length > 0 && decision === "create_new" && newPersonData && onRefreshMatches && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={refreshingMatches}
              onClick={async () => {
                setRefreshingMatches(true)
                setRefreshMatchesError(null)
                try {
                  await onRefreshMatches(newPersonData)
                } catch (error) {
                  setRefreshMatchesError(error instanceof Error ? error.message : "Unable to refresh person matches.")
                } finally {
                  setRefreshingMatches(false)
                }
              }}
            >
              {refreshingMatches ? <IconLoader2 className="mr-1 size-4 animate-spin" /> : <IconRefresh className="mr-1 size-4" />}
              Check current CMS people again
            </Button>
          )}
          <RadioGroup
            value={currentValue}
            onValueChange={(val) => {
              if (val === "create_new") {
                onDecisionChange("create_new", null)
              } else if (val.startsWith("existing_")) {
                onDecisionChange("use_existing", val.replace("existing_", ""))
              }
            }}
            className="mt-2 gap-3"
          >
            {matches.map((match) => (
              <label
                key={match.personId}
                htmlFor={`match-${title}-${match.personId}`}
                className={cn(
                  "flex cursor-pointer flex-col gap-2 rounded-lg border p-3 transition-colors hover:bg-accent/50",
                  currentValue === `existing_${match.personId}` && "border-primary bg-accent/40",
                )}
              >
                <div className="flex items-start gap-3">
                  <RadioGroupItem value={`existing_${match.personId}`} id={`match-${title}-${match.personId}`} className="mt-0.5" />
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <ConfidenceBadge confidence={match.confidence} />
                      <span className="font-medium">
                        {match.firstName && match.lastName
                          ? formatPersonName(match)
                          : match.matchType}
                      </span>
                      {match.dateOfBirth && (
                        <span className="text-muted-foreground text-sm">DOB: {formatDate(match.dateOfBirth)}</span>
                      )}
                      {authorizedIdentifiers?.[match.personId] ? (
                        <span className="text-muted-foreground text-sm">SSN: {authorizedIdentifiers[match.personId]}</span>
                      ) : match.ssnLast4 ? (
                        <span className="text-muted-foreground text-sm">SSN last 4: •••-••-{match.ssnLast4}</span>
                      ) : null}
                    </div>
                    <p className="text-muted-foreground text-xs">
                      {match.matchType} · Matched on: {match.matchedFields.join(", ")}
                    </p>
                  </div>
                </div>
                {match.previousApplications && match.previousApplications.length > 0 && (
                  <div className="ml-8 rounded-md bg-muted/30 p-2">
                    <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wide">
                      Previous Applications ({match.previousApplications.length})
                    </p>
                    <div className="space-y-0.5">
                      {match.previousApplications.map((app) => (
                        <button
                          key={app.benefitId}
                          type="button"
                          className="block text-left text-xs text-primary underline-offset-2 hover:underline"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onViewBenefit?.(app.benefitId) }}
                        >
                          {app.fileNumber > 0 ? `File #${app.fileNumber}` : "File number unavailable"} · PY {app.programYear}{formatPseApplicationType(app.pseApplicationType) ? ` · ${formatPseApplicationType(app.pseApplicationType)}` : ""} · {app.address}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </label>
            ))}
            <div
              className={cn(
                "rounded-lg border border-dashed p-3 transition-colors hover:bg-accent/50",
                currentValue === "create_new" && "border-primary bg-accent/40",
              )}
            >
              <label htmlFor={`match-${title}-create-new`} className="flex cursor-pointer items-center gap-3">
                <RadioGroupItem value="create_new" id={`match-${title}-create-new`} />
                <span className="flex items-center gap-2">
                  <IconCirclePlus className="size-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Create New Person</span>
                </span>
              </label>
              {decision === "create_new" && onNewPersonDataChange && (
                <NewPersonForm
                  data={newPersonData ?? emptyNewPersonData(csvData)}
                  onChange={onNewPersonDataChange}
                  idPrefix={`match-${title}`}
                  canEnterFullIdentifiers={canEnterFullIdentifiers === true}
                />
              )}
            </div>
          </RadioGroup>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Collapsible Data Section ──────────────────────────────────────────

function DataSection({
  title,
  icon,
  children,
  defaultOpen = false,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left transition-colors hover:bg-accent">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium">{title}</span>
        </div>
        {open ? <IconChevronDown className="size-4" /> : <IconChevronRight className="size-4" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-2 pt-1">
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}

// ── Application Data Comparison Table ─────────────────────────────────

interface ComparisonRow {
  field: string
  pseValue: string
  cmsValue: string
  category: string
}

/** Read-only benefit detail returned by the existing authorized benefit projection. */
interface MatchedBenefitPerson {
  id?: string
  firstName?: string
  lastName?: string
  middleName?: string
  birthDate?: string
  dateOfBirth?: string
  socialSecurityNumber?: unknown
}

interface MatchedBenefit extends PriorHouseholdComparison {
  peopleInHousehold?: number
  amountEligible?: unknown
  pseBenefitDetails?: { monthlyIncome?: unknown; energyType?: number | string | null } | null
  personCollection?: MatchedBenefitPerson[]
  addresses?: Record<string, { address?: string; city?: string; state?: string; zipCode?: string }>
  benefitGrants?: unknown
}

function ApplicationDataComparison({
  appData,
  matchDoc,
  authorizedIdentifiers,
}: {
  appData: PseOnlineRecordDetail["applicationData"]
  matchDoc: MatchDocument | null
  authorizedIdentifiers?: Record<string, string>
}) {
  // The selected person match drives the CMS (right) column. Computed before the early return so the
  // benefit fetch hook below always runs (rules of hooks).
  const holderDecision = matchDoc?.accountHolder.decision
  const isExplicitMatch = holderDecision === "use_existing" && !!matchDoc?.accountHolder.selectedPersonId
  // Only an explicit staff selection may populate CMS values. A top candidate is not a decision and
  // must not silently look like the selected person or selected prior file.
  const selectedMatch = isExplicitMatch
    ? matchDoc!.accountHolder.matches.find((m) => m.personId === matchDoc!.accountHolder.selectedPersonId)
    : null
  // Most recent previous application from the selected person match.
  const mostRecentApp = selectedMatch?.previousApplications?.[0]
  const matchedBenefitId = mostRecentApp?.benefitId

  // Fetch that benefit so the comparison can show the matched person's existing household / income /
  // energy / eligibility — none of which is carried in the match document.
  const [loadedBenefit, setLoadedBenefit] = useState<{ id: string; data: MatchedBenefit } | null>(null)
  const matchedBenefit = currentBenefitData(matchedBenefitId, loadedBenefit)
  useEffect(() => {
    if (!matchedBenefitId) {
      setLoadedBenefit(null)
      return
    }
    let active = true
    api
      .get<MatchedBenefit>(`/api/BenefitApplications/${matchedBenefitId}`)
      .then((b) => { if (active) setLoadedBenefit({ id: matchedBenefitId, data: b }) })
      .catch(() => { if (active) setLoadedBenefit(null) })
    return () => { active = false }
  }, [matchedBenefitId])

  if (!appData) return null

  // New-person form values are not CMS values and are intentionally not copied into this comparison.
  const cmsName = selectedMatch ? formatPersonName(selectedMatch) : "Not available"
  const cmsDob = selectedMatch?.dateOfBirth ? formatDate(selectedMatch.dateOfBirth) : "Not available"
  const cmsSsn = (selectedMatch && authorizedIdentifiers?.[selectedMatch.personId])
    ?? (selectedMatch?.ssnLast4 ? `•••-••-${selectedMatch.ssnLast4} (last 4 only)` : "No CMS SSN available")

  const cmsHouseholdMembers = matchedBenefit?.personCollection?.length
    ? matchedBenefit.personCollection
      .map((person) => {
        const name = formatPersonName(person) === "—" ? "Unnamed person" : formatPersonName(person)
        const dob = person.birthDate ?? person.dateOfBirth
        return dob ? `${name} (DOB ${formatDate(dob)})` : name
      })
      .join("; ")
    : "Not available"
  const cmsHouseholdCount = matchedBenefit && typeof matchedBenefit.peopleInHousehold === "number"
    ? String(matchedBenefit.peopleInHousehold)
    : matchedBenefit?.personCollection
      ? String(matchedBenefit.personCollection.length)
      : "Not available"
  const cmsAddress = mostRecentApp?.address
    ?? (matchedBenefit?.addresses?.["Residential"]
      ? [matchedBenefit.addresses["Residential"].address, matchedBenefit.addresses["Residential"].city, matchedBenefit.addresses["Residential"].state, matchedBenefit.addresses["Residential"].zipCode].filter(Boolean).join(", ")
      : "Not available")
  const cmsAccountNumbers = getBenefitAccountNumbers(matchedBenefit)
  const helpEligibility = priorHelpEligibility(matchedBenefit, mostRecentApp?.programYear)

  const rows: ComparisonRow[] = [
    // Person Info
    {
      category: "Person",
      field: "Name",
      pseValue: formatPersonName({ firstName: appData.accountHolder.firstName, middleName: appData.accountHolder.middleInitial, lastName: appData.accountHolder.lastName }),
      cmsValue: cmsName,
    },
    {
      category: "Person",
      field: "Date of Birth",
      pseValue: formatDate(appData.accountHolder.dateOfBirth),
      cmsValue: cmsDob,
    },
    {
      category: "Person",
      field: selectedMatch && authorizedIdentifiers?.[selectedMatch.personId] ? "SSN" : "SSN Last 4",
      pseValue: appData.accountHolder.ssnLast4 ? `•••-••-${appData.accountHolder.ssnLast4}` : "—",
      cmsValue: cmsSsn,
    },
    // Address
    {
      category: "Address",
      field: "Service Address",
      pseValue: formatAddress(appData.serviceAddress),
      cmsValue: cmsAddress,
    },
    {
      category: "Address",
      field: "Mailing Address",
      pseValue: formatAddress(appData.mailingAddress),
      cmsValue: priorMailingAddress(matchedBenefit),
    },
    // Household
    {
      category: "Household",
      field: "Household Members",
      pseValue: String(appData.household.householdMembersCount),
      cmsValue: cmsHouseholdCount,
    },
    {
      category: "Household",
      field: "CMS Household Members",
      pseValue: "Not captured in PSE household summary",
      cmsValue: cmsHouseholdMembers,
    },
    {
      category: "Household",
      field: "Monthly Income",
      pseValue: formatCurrency(appData.household.monthlyIncome),
      cmsValue: formatBenefitMoney(matchedBenefit?.pseBenefitDetails?.monthlyIncome),
    },
    {
      category: "Household",
      field: "Adjusted Gross Income",
      pseValue: formatCurrency(appData.household.adjustedGrossIncome),
      cmsValue: "Not available",
    },
    {
      category: "Household",
      field: "CMS Adjusted Monthly Income",
      pseValue: "Not directly comparable to source Adjusted Gross Income",
      cmsValue: formatBenefitMoney(matchedBenefit?.monthlyAdjustedIncome),
    },
    ...([
      ["Age 0–2", appData.household.numberOfPersonsAge0to2, matchedBenefit?.age0to2],
      ["Age 3–5", appData.household.numberOfPersonsAge3to5, matchedBenefit?.age3to5],
      ["Age 6–17", appData.household.numberOfPersonsAge6to17, matchedBenefit?.age6to17],
      ["Age 18–59", appData.household.numberOfPersonsAge18to59, matchedBenefit?.age18to59],
      ["Age 60+", appData.household.numberOfPersonsAge60Plus, matchedBenefit?.age60Plus],
      ["Persons with Disabilities", appData.household.numberOfPersonsDisabled, matchedBenefit?.disabled],
    ] as const).map(([field, source, prior]) => ({
      category: "Household", field, pseValue: priorHouseholdCount(source), cmsValue: priorHouseholdCount(prior),
    })),
    // Account
    {
      category: "Account",
      field: "Account Number(s)",
      pseValue: appData.accountHolder.accountNumber ?? "—",
      cmsValue: cmsAccountNumbers,
    },
    {
      category: "Account",
      field: "Account Balance",
      pseValue: formatCurrency(appData.accountHolder.accountBalance),
      cmsValue: "Not available",
    },
    // Energy
    {
      category: "Energy",
      field: "Energy Type",
      pseValue: formatEnergyType(appData.energyInfo.energyType),
      cmsValue: priorPseEnergyType(matchedBenefit?.pseBenefitDetails?.energyType),
    },
    {
      category: "Energy",
      field: "Annual Electric",
      pseValue: formatCurrency(appData.energyInfo.annualElectricAmount),
      cmsValue: "Not available",
    },
    {
      category: "Energy",
      field: "Annual Gas",
      pseValue: formatCurrency(appData.energyInfo.annualGasAmount),
      cmsValue: "Not available",
    },
    // Eligibility
    {
      category: "Eligibility",
      field: "PSE Help Eligible",
      pseValue: appData.eligibility.isPseHelpEligible ? "Yes" : "No",
      cmsValue: helpEligibility.eligibility,
    },
    {
      category: "Eligibility",
      field: "PSE Help Eligible Amount",
      pseValue: formatCurrency(appData.eligibility.eligibilityAmount),
      cmsValue: helpEligibility.amount,
    },
  ]

  // Determine CMS column header text
  const cmsLabel = holderDecision === "create_new"
    ? "CMS Value (No Existing Person)"
    : isExplicitMatch
      ? "CMS Value (Selected)"
      : "CMS Value (Select a Person)"

  // Group rows by category
  const categories = [...new Set(rows.map((r) => r.category))]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <IconArrowsExchange className="size-5" />
          Application Data Comparison
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground mb-3 text-sm">
          CMS values are read-only reference data from the selected prior file{mostRecentApp ? ` (PY ${mostRecentApp.programYear})` : ""}; age counts describe that prior file, not today's household. Nothing in this comparison copies household members or changes a person designation. Source Adjusted Gross Income is not the same measure as CMS adjusted monthly income. Current account balance and comparable annual billed electric/gas costs are not available from this projection; calculator usage totals may include backup or surrogate costs and are not substituted. HELP eligibility requires one recorded determination for the prior file's year, not the combined all-program total.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="py-2 pr-4 text-left font-medium">Field</th>
                <th className="py-2 pr-4 text-left font-medium">PSE Online Value</th>
                <th className="py-2 text-left font-medium">{cmsLabel}</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => {
                const catRows = rows.filter((r) => r.category === cat)
                return (
                  <Fragment key={cat}>
                    <tr>
                      <td colSpan={3} className="pt-3 pb-1">
                        <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">{cat}</span>
                      </td>
                    </tr>
                    {catRows.map((row) => {
                      const differs = !["—", "Not available", "No CMS SSN available"].includes(row.cmsValue) && row.pseValue !== row.cmsValue
                      return (
                        <tr key={row.field} className="border-b border-border/50">
                          <td className="text-muted-foreground py-1.5 pr-4">{row.field}</td>
                          <td className="py-1.5 pr-4 font-medium">{row.pseValue}</td>
                          <td className={cn("py-1.5 font-medium", differs && "text-yellow-600 dark:text-yellow-400")}>
                            {row.cmsValue}
                          </td>
                        </tr>
                      )
                    })}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Income Assignment Section ─────────────────────────────────────────

function IncomeAssignmentSection({
  matchDoc,
  hasCoApplicant,
  onChange,
}: {
  matchDoc: MatchDocument
  hasCoApplicant: boolean
  onChange: (value: MatchDocument["incomeAssignment"]) => void
}) {
  const household = matchDoc.householdMembers.length
  const currentValue = matchDoc.incomeAssignment ?? ""

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <IconScale className="size-5" />
          Income Assignment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-muted-foreground text-sm">
          Who should the total household income be assigned to?
        </p>
        <RadioGroup
          value={currentValue}
          onValueChange={(val) => onChange(val as MatchDocument["incomeAssignment"])}
          className="gap-3"
        >
          {!matchDoc.accountHolderHouseholdExclusionReason && <label
            htmlFor="income-account-holder"
            className={cn(
              "flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-accent/50",
              currentValue === "accountHolder" && "border-primary bg-accent/40",
            )}
          >
            <RadioGroupItem value="accountHolder" id="income-account-holder" />
            <div>
              <span className="text-sm font-medium">Account Holder</span>
              <p className="text-muted-foreground text-xs">Assign all income to the primary applicant</p>
            </div>
          </label>}

          {hasCoApplicant && (
            <label
              htmlFor="income-co-applicant"
              className={cn(
                "flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-accent/50",
                currentValue === "coApplicant" && "border-primary bg-accent/40",
              )}
            >
              <RadioGroupItem value="coApplicant" id="income-co-applicant" />
              <div>
                <span className="text-sm font-medium">Co-Applicant</span>
                <p className="text-muted-foreground text-xs">Assign all income to the co-applicant</p>
              </div>
            </label>
          )}

          {household > 0 && (
            <label
              htmlFor="income-split"
              className={cn(
                "flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-accent/50",
                currentValue === "split" && "border-primary bg-accent/40",
              )}
            >
              <RadioGroupItem value="split" id="income-split" />
              <div>
                <span className="text-sm font-medium">Split Equally</span>
                <p className="text-muted-foreground text-xs">
                  Split income equally among all household members
                </p>
              </div>
            </label>
          )}

          <label
            htmlFor="income-custom"
            className={cn(
              "flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-accent/50",
              currentValue === "custom" && "border-primary bg-accent/40",
            )}
          >
            <RadioGroupItem value="custom" id="income-custom" />
            <div>
              <span className="text-sm font-medium">Custom Allocation</span>
              <p className="text-muted-foreground text-xs">Manually allocate income across household members</p>
            </div>
          </label>
        </RadioGroup>
      </CardContent>
    </Card>
  )
}

function ApplicantSelectionSection({
  matchDoc,
  onChange,
}: {
  matchDoc: MatchDocument
  onChange: (selectedPersonId: string | null, selectedPersonSlot: string | null, relationships: Record<string, number>) => void
}) {
  const people: Array<{ slot: string; label: string; decision: PersonSection["decision"] | HouseholdMemberSection["decision"]; selectedPersonId: string | null; matches: PersonMatch[] }> = [
    ...(!matchDoc.accountHolderHouseholdExclusionReason ? [{ slot: "accountHolder", label: "Account holder", decision: matchDoc.accountHolder.decision, selectedPersonId: matchDoc.accountHolder.selectedPersonId, matches: matchDoc.accountHolder.matches }] : []),
    ...(matchDoc.coCustomer ? [{ slot: "coCustomer", label: "Co-customer", decision: matchDoc.coCustomer.decision, selectedPersonId: matchDoc.coCustomer.selectedPersonId, matches: matchDoc.coCustomer.matches }] : []),
    ...matchDoc.householdMembers
      .filter(member => member.decision !== "exclude")
      .map(member => ({
        slot: householdMemberSlot(member.index),
        label: `Household member ${member.index + 1}`,
        decision: member.decision,
        selectedPersonId: member.selectedPersonId,
        matches: member.matches,
      })),
  ]
  const accountHolderId = matchDoc.accountHolder.decision === "use_existing" ? matchDoc.accountHolder.selectedPersonId : null
  const invalidated = matchDoc.applicantSelection?.invalidated === true
  const savedSlot = matchDoc.applicantSelection?.selectedPersonSlot
  const savedId = matchDoc.applicantSelection?.selectedPersonId
  const selectedSlot = invalidated
    ? null
    : savedSlot ?? (savedId ? people.find(person => person.selectedPersonId === savedId)?.slot : null) ?? "accountHolder"
  const selectedPerson = selectedSlot ? people.find(person => person.slot === selectedSlot) : undefined
  const selectedId = selectedPerson?.decision === "use_existing" ? selectedPerson.selectedPersonId : null
  const isAlternate = selectedSlot !== null && selectedSlot !== "accountHolder"
  const reviewedSlots = reviewedApplicantSlots(matchDoc as unknown as HouseholdReviewDocument)
  // The selected applicant is a read-only Self relationship, but it still belongs in the
  // persisted map. Keep this projection visible to every relationship update so the server
  // receives the same complete household map that the UI displays.
  const relationships = matchDoc.applicantSelection?.relationships ?? {}
  const selectableCandidates = people.filter(person => reviewedSlots.includes(person.slot) && person.slot !== "accountHolder")
  const options = selectOptions(relationshipLabels).filter(option => option.value !== "0")
  const bulkRelationships = selectedSlot ? fillUnansweredRelationships(matchDoc as unknown as HouseholdReviewDocument, selectedSlot) : null
  const unansweredCount = people.filter(person => person.slot !== selectedSlot &&
    (relationships[person.slot] == null || relationships[person.slot] === 255)).length

  return <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-base">CMS Applicant</CardTitle>
      <p className="text-muted-foreground text-sm">The account holder remains the bill identity. Relationships are left unanswered unless staff explicitly records them. Existing choices are preserved.</p>
    </CardHeader>
    <CardContent className="space-y-4">
      {invalidated && (
        <Alert variant="destructive">
          <IconAlertTriangle className="size-4" />
          <AlertDescription>
            The previously selected applicant is no longer included. Explicitly choose the account holder or another included applicant before saving and creating.
          </AlertDescription>
        </Alert>
      )}
      {matchDoc.applicantSelection?.relationshipReviewRequired && !invalidated && (
        <Alert>
          <IconAlertTriangle className="size-4" />
          <AlertDescription>
            Household membership changed. Review every relationship below and confirm the mapping before creating.
          </AlertDescription>
        </Alert>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="pse-cms-applicant">Applicant</Label>
        <Select value={invalidated ? "" : selectedSlot === "accountHolder" ? "accountHolder" : `slot:${selectedSlot}`} onValueChange={value => {
          if (value === "accountHolder") onChange(null, null, {})
          else {
            const slot = value.replace(/^slot:/, "")
            const person = people.find(candidate => candidate.slot === slot)
            onChange(person?.decision === "use_existing" ? person.selectedPersonId : null, slot, { [slot]: 0 })
          }
        }}>
          <SelectTrigger id="pse-cms-applicant"><SelectValue placeholder="Choose a reviewed person" /></SelectTrigger>
          <SelectContent>
            {invalidated && <SelectItem value="invalidated" disabled>Selection requires review</SelectItem>}
            {!matchDoc.accountHolderHouseholdExclusionReason && <SelectItem value="accountHolder">Account holder (default)</SelectItem>}
            {selectableCandidates.map(candidate => {
              const match = candidate.selectedPersonId ? candidate.matches.find(item => item.personId === candidate.selectedPersonId) : undefined
              return <SelectItem key={candidate.slot} value={`slot:${candidate.slot}`}>{match ? formatPersonName(match) : candidate.label} {candidate.decision === "create_new" ? "(new person)" : ""}</SelectItem>
            })}
          </SelectContent>
        </Select>
        {selectableCandidates.length === 0 && <p className="text-xs text-muted-foreground">Review an included household person decision before choosing an alternate applicant.</p>}
        {isAlternate && selectedId === null && <p className="text-xs text-muted-foreground">This create-new person will be resolved by its household slot on the server.</p>}
      </div>

      {selectedSlot && !invalidated && people.length > 1 && <div className="space-y-3 rounded-md border bg-muted/20 p-3">
        <p className="text-sm font-medium">Relationships to the selected applicant</p>
        <p className="text-xs text-muted-foreground">The selected applicant is Self. If you record relationships here, complete every included person's relationship before creating.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button type="button" size="sm" variant="outline" disabled={!bulkRelationships || unansweredCount === 0}>
              Set unanswered relationships to Other Relative
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm household relationships</AlertDialogTitle>
              <AlertDialogDescription>
                Set {unansweredCount} unanswered non-applicant relationship(s) to Other Relative?
                Confirm only if this accurately describes these household members. These choices affect family composition.
                Existing relationships and excluded people will not be changed. Save the decisions afterward.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction disabled={!bulkRelationships || unansweredCount === 0} onClick={() => {
                if (selectedSlot && bulkRelationships) onChange(selectedId, selectedSlot, bulkRelationships)
              }}>Confirm Other Relative</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {!bulkRelationships && <p className="text-xs text-muted-foreground">Review every included person's identity decision before using bulk assignment.</p>}
        {people.map(person => person.slot === selectedSlot ? (
          <div key={person.slot} className="flex items-center justify-between gap-3 text-sm">
            <Label htmlFor={`pse-relationship-${person.slot}`}>{person.label}</Label>
            <span id={`pse-relationship-${person.slot}`} className="font-medium">Self — applicant</span>
          </div>
        ) : (
          <div key={person.slot} className="space-y-1">
            <Label htmlFor={`pse-relationship-${person.slot}`}>{person.label}</Label>
            <Select value={relationships[person.slot] == null ? "unanswered" : String(relationships[person.slot])} onValueChange={value => onChange(selectedId, selectedSlot, { ...relationships, [selectedSlot]: 0, [person.slot]: Number(value) })}>
              <SelectTrigger id={`pse-relationship-${person.slot}`}><SelectValue placeholder="Relationship to applicant" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unanswered">Choose relationship</SelectItem>
                {options.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
        {matchDoc.applicantSelection?.relationshipReviewRequired && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onChange(selectedId, selectedSlot, { ...relationships, ...(selectedSlot ? { [selectedSlot]: 0 } : {}) })}
          >
            Confirm relationship mapping
          </Button>
        )}
        {accountHolderId === null && isAlternate && <p className="text-xs text-amber-700">The account holder will be created from its reviewed slot and remains the bill identity.</p>}
      </div>}
    </CardContent>
  </Card>
}

// ── Main Page Component ───────────────────────────────────────────────

export default function PseOnlineDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const permissions = useAppSelector((state) => state.permissions.permissions)
  const canUpdateStaffProgress = canUpdateIndividualStaffProgress(permissions)
  const [distributionCapability, setDistributionCapability] = useState<PseOnlineDistributionCapability | null>(null)
  const [distributionStaff, setDistributionStaff] = useState<PseOnlineDetailStaffOption[]>([])
  const canManageDistribution = distributionCapability?.canManageDistribution === true && canManagePseDistribution(permissions)

  const [record, setRecord] = useState<PseOnlineRecordDetail | null>(null)
  const [matchDocResponse, setMatchDocResponse] = useState<MatchDocumentResponse | null>(null)
  const [matchDoc, setMatchDoc] = useState<MatchDocument | null>(null)
  const matchDocRevision = useRef(0)
  const liveSourceId = useRef(id)
  const validatedRevision = useRef<number | null>(null)
  const creationLocked = useRef(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [validating, setValidating] = useState(false)
  const [creating, setCreating] = useState(false)
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null)
  const [createResult, setCreateResult] = useState<CreateBenefitResult | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [reprocessing, setReprocessing] = useState(false)
  const [reprocessMessage, setReprocessMessage] = useState<string | null>(null)
  const [reloadNonce, setReloadNonce] = useState(0)
  // Invalidate pending editor requests on navigation/reload before passive loading starts.
  useLayoutEffect(() => {
    liveSourceId.current = id
    matchDocRevision.current += 1
  }, [id, reloadNonce])
  const [staffProgressStatus, setStaffProgressStatus] = useState<string>("0")
  const [staffProgressHistory, setStaffProgressHistory] = useState<Array<{
    id: string
    fromStatus: number | string
    toStatus: number | string
    previousVersion: number
    newVersion: number
    changedAt: string
    changedBy: string
    notes?: string | null
  }>>([])
  const [staffProgressSaving, setStaffProgressSaving] = useState(false)
  const [staffProgressMessage, setStaffProgressMessage] = useState<string | null>(null)
  const [assignmentHistory, setAssignmentHistory] = useState<PseOnlineDetailAssignmentHistory[]>([])
  const [assignmentStaffId, setAssignmentStaffId] = useState("unassigned")
  const [assignmentSaving, setAssignmentSaving] = useState(false)
  const [assignmentMessage, setAssignmentMessage] = useState<string | null>(null)
  const registerEdits = useBenefitEditRegistration()
  const [creationHandoffCompleted, setCreationHandoffCompleted] = useState(false)
  const savedDocument = useMemo(() => {
    try { return JSON.stringify(JSON.parse(matchDocResponse?.matchDocument ?? "null")) }
    catch { return "null" }
  }, [matchDocResponse?.matchDocument])
  const hasUnsavedDecisions = matchDoc !== null && JSON.stringify(matchDoc) !== savedDocument
  useLayoutEffect(() => { registerEdits(!creationHandoffCompleted && (hasUnsavedDecisions || saving || validating || creating)) },
    [creationHandoffCompleted, hasUnsavedDecisions, saving, validating, creating, registerEdits])

  // Benefit preview dialog state
  const [benefitPreviewId, setBenefitPreviewId] = useState<string | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [benefitPreview, setBenefitPreview] = useState<any | null>(null)
  const [benefitPreviewLoading, setBenefitPreviewLoading] = useState(false)

  // PSE Companion snapshot & dispatch state
  const [billHolderExclusionReason, setBillHolderExclusionReason] = useState("")
  const [snapshotReview, setSnapshotReview] = useState<SnapshotReview | null>(null)
  const [companionStatus, setCompanionStatus] = useState<CompanionStatusDto | null>(null)
  const [snapshotDecisions, setSnapshotDecisions] = useState<Record<number, SnapshotMemberDecision>>({})
  const [dispatchingCompanion, setDispatchingCompanion] = useState(false)
  const [applyingSnapshot, setApplyingSnapshot] = useState(false)
  const [companionMessage, setCompanionMessage] = useState<string | null>(null)

  // Load record and match document
  useEffect(() => {
    if (!id) return
    let active = true
    setLoading(true)
    setError(null)

    Promise.all([
      api.get<PseOnlineRecordDetail>(`/api/pse-online/records/${id}`),
      api.get<MatchDocumentResponse>(`/api/pse-online/records/${id}/match-document`).catch(() => null),
      api.get<PseOnlineDetailStaffProgressHistory[]>(`/api/pse-online/records/${id}/staff-progress/history`).catch(() => []),
      api.get<PseOnlineDetailAssignmentHistory[]>(`/api/pse-online/records/${id}/assignment/history`).catch(() => []),
      api.get<SnapshotReview>(`/api/pse-online/records/${id}/snapshot`).catch(() => null),
      api.get<CompanionStatusDto>("/api/pse-online/snapshots/companion/status").catch(() => null),
    ])
      .then(([recordData, matchData, history, ownershipHistory, snapReview, compStatus]) => {
        if (!active) return
        matchDocRevision.current += 1
        setRecord(recordData)
        setAssignmentHistory(ownershipHistory)
        setAssignmentStaffId(recordData.assignedStaffId ?? "unassigned")
        setStaffProgressHistory(history)
        setStaffProgressStatus(String(staffProgressStatusValue(recordData.staffProgressStatus)))
        setSnapshotReview(snapReview)
        setCompanionStatus(compStatus)
        let parsedDoc: MatchDocument | null = null
        if (matchData) {
          setMatchDocResponse(matchData)
          try {
            parsedDoc = JSON.parse(matchData.matchDocument)
            setMatchDoc(parsedDoc)
          } catch {
            setMatchDoc(null)
          }
        } else {
          setMatchDocResponse(null)
          setMatchDoc(null)
        }
        if (snapReview) {
          const initial: Record<number, SnapshotMemberDecision> = {}
          snapReview.household.forEach((member, idx) => {
            const saved = parsedDoc?.snapshotEnrichment?.snapshotId === snapReview.receipt.snapshotId
              ? parsedDoc.snapshotEnrichment.decisions.find(d => d.memberIndex === idx)
              : undefined
            initial[idx] = saved ?? {
              memberIndex: idx,
              decision: "no",
              selectedPersonId: member.candidates[0]?.personId ?? null,
            }
          })
          setSnapshotDecisions(initial)
        }
      })
      .catch((err) => { if (active) setError(err instanceof Error ? err.message : "Failed to load record") })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [id, reloadNonce])

  useEffect(() => {
    api.get<PseOnlineDistributionCapability>("/api/pse-online/distribution/capability")
      .then((capability) => {
        setDistributionCapability(capability)
        if (capability.canViewStaffOptions) {
          return api.get<PseOnlineDetailStaffOption[]>("/api/pse-online/distribution/staff-options")
        }
        return []
      })
      .then(setDistributionStaff)
      .catch(() => {
        setDistributionCapability(null)
        setDistributionStaff([])
      })
  }, [])

  // Fetch benefit preview when dialog opens
  useEffect(() => {
    if (!benefitPreviewId) {
      setBenefitPreview(null)
      return
    }
    setBenefitPreviewLoading(true)
    setBenefitPreview(null)
    api.get(`/api/BenefitApplications/${benefitPreviewId}`)
      .then((data) => setBenefitPreview(data))
      .catch(() => setBenefitPreview(null))
      .finally(() => setBenefitPreviewLoading(false))
  }, [benefitPreviewId])

  const handleReprocess = useCallback(async () => {
    if (!id) return
    setReprocessing(true)
    setReprocessMessage(null)
    setError(null)
    try {
      await api.post(`/api/pse-online/records/${id}/reprocess`)
      // Single-record matching is fast; poll for the rebuilt match document.
      const deadline = Date.now() + 45000
      let built = false
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 1500))
        // Staff-owned documents now remain present while matching runs. Presence alone is
        // not completion: wait until the matcher advances the record's processed timestamp.
        const refreshed = await api
          .get<PseOnlineRecordDetail>(`/api/pse-online/records/${id}`)
          .catch(() => null)
        if (refreshed?.processedAt && refreshed.processedAt !== record?.processedAt) {
          built = true
          break
        }
      }
      if (built) {
        setReprocessMessage("Match document rebuilt.")
        setReloadNonce((n) => n + 1)
      } else {
        setReprocessMessage("Matching queued \u2014 it's taking longer than expected. Try refreshing shortly.")
      }
    } catch (err) {
      setReprocessMessage(err instanceof Error ? err.message : "Failed to start reprocessing")
    } finally {
      setReprocessing(false)
    }
  }, [id, record?.processedAt])

  const handleStaffProgressChange = useCallback(async (status: string) => {
    if (!id || !record || !canUpdateStaffProgress) return
    setStaffProgressSaving(true)
    setStaffProgressMessage(null)
    try {
      await api.patch(`/api/pse-online/records/${id}/staff-progress`, {
        status: Number(status),
        expectedVersion: record.staffProgressVersion,
      })
      // Refresh only the progress read model. Reloading the whole detail here resets the
      // unsaved person decisions/income editor, losing staff work unrelated to this control.
      const [updatedRecord, history] = await Promise.all([
        api.get<PseOnlineRecordDetail>(`/api/pse-online/records/${id}`),
        api.get<PseOnlineDetailStaffProgressHistory[]>(`/api/pse-online/records/${id}/staff-progress/history`),
      ])
      setRecord(updatedRecord)
      setStaffProgressStatus(String(staffProgressStatusValue(updatedRecord.staffProgressStatus)))
      setStaffProgressHistory(history)
      setStaffProgressMessage("Staff progress saved.")
    } catch (error) {
      setStaffProgressStatus(String(staffProgressStatusValue(record.staffProgressStatus)))
      setStaffProgressMessage(error instanceof Error ? error.message : "Unable to save staff progress.")
    } finally {
      setStaffProgressSaving(false)
    }
  }, [id, record, canUpdateStaffProgress])

  const handleAssignmentChange = useCallback(async (staffId: string) => {
    if (!id || !record || !matchDocResponse || !canManageDistribution) return
    setAssignmentStaffId(staffId)
    setAssignmentSaving(true)
    setAssignmentMessage(null)
    try {
      await api.patch(`/api/pse-online/records/assignment`, {
        updates: [{
          recordId: id,
          expectedAssignmentVersion: record.assignmentVersion,
          assignedStaffId: staffId === "unassigned" ? null : staffId,
        }],
      })
      // Refresh ownership metadata only. Keep matchDoc state untouched so a dirty editor cannot
      // be replaced by an assignment refresh.
      const [updatedRecord, updatedDoc, history] = await Promise.all([
        api.get<PseOnlineRecordDetail>(`/api/pse-online/records/${id}`),
        api.get<MatchDocumentResponse>(`/api/pse-online/records/${id}/match-document`),
        api.get<PseOnlineDetailAssignmentHistory[]>(`/api/pse-online/records/${id}/assignment/history`),
      ])
      setRecord(updatedRecord)
      setMatchDocResponse(updatedDoc)
      setAssignmentHistory(history)
      setAssignmentStaffId(updatedRecord.assignedStaffId ?? "unassigned")
      setAssignmentMessage("Assignment saved.")
    } catch (error) {
      setAssignmentStaffId(record.assignedStaffId ?? "unassigned")
      setAssignmentMessage(error instanceof Error ? error.message : "Assignment was not saved. Reload and try again.")
    } finally {
      setAssignmentSaving(false)
    }
  }, [id, record, matchDocResponse, canManageDistribution])

  const handleDispatchCompanion = useCallback(async () => {
    if (!id || !record || dispatchingCompanion) return
    setDispatchingCompanion(true)
    setCompanionMessage("Requesting snapshot export from your PSE Companion…")
    try {
      const result = await api.post<CompanionDispatchResult>("/api/pse-online/snapshots/companion/dispatch", {
        applicationId: String(record.pseApplicationId),
        programYear: record.programYear,
        agencyId: record.applicationData?.agencyId ?? 77,
      })
      if (result.status === "stored" || result.status === "completed") {
        setCompanionMessage("Snapshot exported and uploaded to CMS.")
        setReloadNonce((n) => n + 1)
      } else {
        setCompanionMessage(result.message ?? `Export finished with status: ${result.status}`)
      }
      const status = await api.get<CompanionStatusDto>("/api/pse-online/snapshots/companion/status").catch(() => null)
      if (status) setCompanionStatus(status)
    } catch (error) {
      setCompanionMessage(error instanceof Error ? error.message : "Unable to request export from PSE Companion.")
    } finally {
      setDispatchingCompanion(false)
    }
  }, [id, record, dispatchingCompanion])

  const handleApplySnapshotDecisions = useCallback(async () => {
    if (!id || !snapshotReview || !matchDocResponse || hasUnsavedDecisions || applyingSnapshot || creationLocked.current) return
    creationLocked.current = true
    setApplyingSnapshot(true)
    setCompanionMessage(null)
    setError(null)
    try {
      const decisions = Object.values(snapshotDecisions)
      const updated = await api.post<MatchDocumentResponse>(`/api/pse-online/records/${id}/snapshot/apply`, {
        snapshotId: snapshotReview.receipt.snapshotId,
        expectedUpdatedAt: matchDocResponse.updatedAt,
        decisions,
      })
      if (liveSourceId.current !== id) return
      matchDocRevision.current += 1
      validatedRevision.current = null
      setValidationResult(null)
      setMatchDocResponse(updated)
      setMatchDoc(JSON.parse(updated.matchDocument))
      setCompanionMessage("Confirmed snapshot decisions applied to the match document.")
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to apply snapshot decisions.")
    } finally {
      creationLocked.current = false
      setApplyingSnapshot(false)
    }
  }, [id, snapshotReview, snapshotDecisions, applyingSnapshot, matchDocResponse, hasUnsavedDecisions])

  // Build the updated match document JSON from current state
  const buildUpdatedMatchDocumentJson = useCallback((): string => {
    if (!matchDoc) return "{}"
    return JSON.stringify(normalizeMatchDocumentForSave(matchDoc))
  }, [matchDoc])

  // Update a person section decision
  const updatePersonDecision = useCallback(
    (section: "accountHolder" | "coCustomer", decision: "use_existing" | "create_new", personId: string | null) => {
      if (creationLocked.current) return
      matchDocRevision.current += 1
      setMatchDoc((prev) => {
        if (!prev) return prev
        if (section === "coCustomer" && !prev.coCustomer) return prev
        const sectionData = prev[section]!
        let newPersonData = sectionData.newPersonData
        if (decision === "create_new" && !newPersonData && sectionData.csvData) {
          newPersonData = {
            firstName: sectionData.csvData.firstName ?? "",
            lastName: sectionData.csvData.lastName ?? "",
            middleName: sectionData.csvData.middleInitial ?? "",
            socialSecurityNumber: "",
            dateOfBirth: sectionData.csvData.dateOfBirth ?? "",
            gender: 5,
          }
        }
        const updated = {
          ...prev,
          [section]: {
            ...sectionData,
            decision,
            selectedPersonId: personId,
            newPersonData: decision === "create_new" ? newPersonData : sectionData.newPersonData,
          },
        }
        return {
          ...updated,
          applicantSelection: reconcileApplicantSelection(updated as unknown as HouseholdReviewDocument, section, false),
        }
      })
      setSaveSuccess(false)
      setValidationResult(null)
      setCreateResult(null)
    },
    [],
  )

  // Update household member decision
  const updateHouseholdDecision = useCallback(
    (index: number, decision: "use_existing" | "create_new" | "exclude", personId: string | null, exclusionReason: string | null = null) => {
      if (creationLocked.current) return
      matchDocRevision.current += 1
      setMatchDoc((prev) => {
        if (!prev) return prev
        const previousMember = prev.householdMembers.find(member => member.index === index)
        const updated = {
          ...prev,
          householdMembers: prev.householdMembers.map((m) => {
            if (m.index !== index) return m
            let newPersonData = m.newPersonData
            if (decision === "create_new" && !newPersonData) {
              newPersonData = emptyNewPersonData()
            }
            return {
              ...m,
              decision,
              selectedPersonId: decision === "exclude" ? null : personId,
              newPersonData: decision === "create_new" ? newPersonData : decision === "exclude" ? null : m.newPersonData,
              exclusionReason: decision === "exclude" ? exclusionReason : null,
            }
          }),
        }
        return {
          ...updated,
          applicantSelection: reconcileApplicantSelection(
            updated as unknown as HouseholdReviewDocument,
            householdMemberSlot(index),
            decision === "exclude" || previousMember?.decision === "exclude",
          ),
        }
      })
      setSaveSuccess(false)
      setValidationResult(null)
      setCreateResult(null)
    },
    [],
  )

  const updateHouseholdExclusionReason = useCallback((index: number, reason: string) => {
    if (creationLocked.current) return
    matchDocRevision.current += 1
    setMatchDoc((prev) => prev ? {
      ...prev,
      householdMembers: prev.householdMembers.map((member) =>
        member.index === index ? { ...member, exclusionReason: reason } : member,
      ),
    } : prev)
    setSaveSuccess(false)
    setValidationResult(null)
    setCreateResult(null)
  }, [])

  const restoreHouseholdMember = useCallback((index: number) => {
    if (creationLocked.current) return
    matchDocRevision.current += 1
    setMatchDoc((prev) => {
      if (!prev) return prev
      const updated = {
        ...prev,
        householdMembers: prev.householdMembers.map((member) =>
          member.index === index
            ? { ...member, decision: null, selectedPersonId: null, newPersonData: null, exclusionReason: null }
            : member,
        ),
      }
      return {
        ...updated,
        applicantSelection: reconcileApplicantSelection(
          updated as unknown as HouseholdReviewDocument,
          householdMemberSlot(index),
          true,
        ),
      }
    })
    setSaveSuccess(false)
    setValidationResult(null)
    setCreateResult(null)
  }, [])

  const selectSectionExistingPerson = useCallback((section: "accountHolder" | "coCustomer", match: PersonMatch) => {
    if (creationLocked.current) return
    matchDocRevision.current += 1
    setMatchDoc((prev) => {
      if (!prev) return prev
      if (section === "coCustomer" && !prev.coCustomer) return prev
      const sectionData = prev[section]!
      const matches = sectionData.matches.some(candidate => candidate.personId === match.personId)
        ? sectionData.matches
        : [...sectionData.matches, match]
      const updated = {
        ...prev,
        [section]: {
          ...sectionData,
          matches,
          decision: "use_existing" as const,
          selectedPersonId: match.personId,
        },
      }
      return {
        ...updated,
        applicantSelection: reconcileApplicantSelection(
          updated as unknown as HouseholdReviewDocument,
          section,
          false,
        ) as MatchDocument["applicantSelection"],
      }
    })
    setSaveSuccess(false)
    setValidationResult(null)
    setCreateResult(null)
  }, [])

  const selectHouseholdExistingPerson = useCallback((index: number, match: PersonMatch) => {
    if (creationLocked.current) return
    matchDocRevision.current += 1
    setMatchDoc((prev) => {
      if (!prev) return prev
      const updated = {
        ...prev,
        householdMembers: prev.householdMembers.map((member) => {
          if (member.index !== index) return member
          const matches = member.matches.some(candidate => candidate.personId === match.personId)
            ? member.matches
            : [...member.matches, match]
          return { ...member, matches, decision: "use_existing", selectedPersonId: match.personId, newPersonData: null, exclusionReason: null }
        }),
      }
      return {
        ...updated,
        applicantSelection: reconcileApplicantSelection(
          updated as unknown as HouseholdReviewDocument,
          householdMemberSlot(index),
          false,
        ),
      }
    })
    setSaveSuccess(false)
    setValidationResult(null)
    setCreateResult(null)
  }, [])

  // Update income assignment
  const updateIncomeAssignment = useCallback(
    (value: MatchDocument["incomeAssignment"]) => {
      if (creationLocked.current) return
      matchDocRevision.current += 1
      setMatchDoc((prev) => {
        if (!prev) return prev
        return { ...prev, incomeAssignment: value }
      })
      setSaveSuccess(false)
      setValidationResult(null)
      setCreateResult(null)
    },
    [],
  )

  // Select an explicit reviewed household applicant. Null preserves the historical account-holder default.
  const updateApplicantSelection = useCallback(
    (selectedPersonId: string | null, selectedPersonSlot: string | null, relationships: Record<string, number>) => {
      if (creationLocked.current) return
      matchDocRevision.current += 1
      setMatchDoc((prev) => {
        if (!prev) return prev
        const normalizedRelationships = selectedPersonSlot
          ? { ...relationships, [selectedPersonSlot]: 0 }
          : {}
        return {
          ...prev,
          applicantSelection: selectedPersonSlot
            ? {
                selectedPersonId,
                selectedPersonSlot,
                accountHolderRelationship: normalizedRelationships.accountHolder ?? null,
                relationships: normalizedRelationships,
                invalidated: false,
                relationshipReviewRequired: false,
              }
            : null,
        }
      })
      setSaveSuccess(false)
      setValidationResult(null)
      setCreateResult(null)
    },
    [],
  )

  // Update new person data for account holder or co-customer
  const updateNewPersonData = useCallback(
    (section: "accountHolder" | "coCustomer", data: NewPersonData) => {
      if (creationLocked.current) return
      matchDocRevision.current += 1
      setValidationResult(null)
      setMatchDoc((prev) => {
        if (!prev) return prev
        if (section === "coCustomer" && !prev.coCustomer) return prev
        return {
          ...prev,
          [section]: {
            ...prev[section]!,
            newPersonData: data,
          },
        }
      })
    },
    [],
  )

  // Explicitly refresh current permitted candidates for entered identity data. This is a
  // deliberate validation action, not a per-keystroke search; the server refreshes its snapshot
  // and returns only names, DOB, confidence, and SSN last four.
  const refreshPersonMatches = useCallback(async (section: "accountHolder" | "coCustomer", data: NewPersonData): Promise<PersonMatch[]> => {
    if (!id || creationLocked.current) return []
    const submittedRevision = matchDocRevision.current
    const submittedContext = { sourceId: id, section, revision: submittedRevision }
    const existingMatches = section === "accountHolder" ? (matchDoc?.accountHolder.matches ?? []) : (matchDoc?.coCustomer?.matches ?? [])
    const refreshed = await api.post<PseOnlinePersonMatchRefreshResponse>(
      `/api/pse-online/records/${id}/match-candidates`,
      {
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: data.dateOfBirth || null,
        socialSecurityNumber: data.socialSecurityNumber || null,
      },
    )
    if (!refreshed.isReady) throw new Error(refreshed.message ?? "Person matching is not ready. Try again shortly.")
    const candidates: PersonMatch[] = reconcileRefreshedMatches(existingMatches, refreshed.candidates.map(candidate => ({
      personId: candidate.personId,
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      dateOfBirth: candidate.dateOfBirth ?? undefined,
      ssnLast4: candidate.ssnLast4 ?? undefined,
      confidence: candidate.confidence,
      matchType: candidate.matchType,
      matchedFields: candidate.matchedFields,
      previousApplications: candidate.previousApplications,
    })))
    if (!acceptMatchRefresh(submittedContext, liveSourceId, matchDocRevision, creationLocked)) return []
    setMatchDoc(current => {
      if (!current || (section === "coCustomer" && !current.coCustomer)) return current
      return {
        ...current,
        [section]: { ...current[section]!, matches: candidates },
      }
    })
    setValidationResult(null)
    setCreateResult(null)
    return candidates
  }, [id, matchDoc])

  // Update new person data for household member
  const updateHouseholdNewPersonData = useCallback(
    (index: number, data: NewPersonData) => {
      if (creationLocked.current) return
      matchDocRevision.current += 1
      setValidationResult(null)
      setMatchDoc((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          householdMembers: prev.householdMembers.map((m) =>
            m.index === index ? { ...m, newPersonData: data } : m,
          ),
        }
      })
    },
    [],
  )

  // Save progress
  const handleSave = useCallback(async () => {
    if (!id || creationLocked.current) return
    setError(null)
    setSaving(true)
    setSaveSuccess(false)
    const submittedRevision = matchDocRevision.current
    const submittedMatchDocument = buildUpdatedMatchDocumentJson()
    try {
      const updated = await api.patch<MatchDocumentResponse>(
        `/api/pse-online/records/${id}/match-document`,
        { matchDocument: submittedMatchDocument },
      )
      if (liveSourceId.current !== id) return
      setMatchDocResponse(updated)
      // A staff edit may arrive while PATCH is pending. Keep that later edit in the editor
      // and let dirty-state detection require a second save; only replace the editor when
      // the response still corresponds to its submitted revision.
      if (matchDocRevision.current === submittedRevision) {
        setMatchDoc(JSON.parse(updated.matchDocument))
      }
      setSaveSuccess(matchDocRevision.current === submittedRevision)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save")
    } finally {
      setSaving(false)
    }
  }, [id, buildUpdatedMatchDocumentJson])

  // Validate then create
  const handleCreateBenefit = useCallback(async () => {
    if (!id || creationLocked.current) return
    validatedRevision.current = null
    setError(null)
    setValidating(true)
    setValidationResult(null)
    setCreateResult(null)

    const submittedRevision = matchDocRevision.current
    const submittedMatchDocument = buildUpdatedMatchDocumentJson()

    try {
      // Save first
      const saved = await api.patch<MatchDocumentResponse>(
        `/api/pse-online/records/${id}/match-document`,
        { matchDocument: submittedMatchDocument },
      )

      setMatchDocResponse(saved)
      if (matchDocRevision.current !== submittedRevision) {
        setError("New edits were made while saving. Save them before creating the application.")
        return
      }
      setMatchDoc(JSON.parse(saved.matchDocument))

      // Validate
      const validation = await api.post<ValidationResult>(`/api/pse-online/records/${id}/validate-for-creation`)
      if (matchDocRevision.current !== submittedRevision) {
        setError("New edits were made during validation. Save and validate them before creating the application.")
        return
      }
      validatedRevision.current = submittedRevision
      setValidationResult(validation)

      if (!validation.isValid) {
        setValidating(false)
        return
      }

      // If warnings exist but still valid, show them but proceed
      if (validation.warnings.length > 0) {
        setValidating(false)
        // Show warnings — user can click Create again to confirm
        return
      }

      // No warnings, create only the exact decision revision that was saved and validated.
      creationLocked.current = true
      setValidating(false)
      setCreating(true)
      const result = await api.post<CreateBenefitResult>(`/api/pse-online/records/${id}/create-benefit`)
      if (result.success && result.benefitId) {
        // The server committed and sealed the matching decisions. Release this editor before
        // navigation; its in-flight flag would otherwise block the successful handoff itself.
        setCreationHandoffCompleted(true)
        registerEdits(false)
        navigate(pseBenefitResumePath(result.benefitId))
        return
      }
      setCreateResult(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create benefit")
    } finally {
      creationLocked.current = false
      setValidating(false)
      setCreating(false)
    }
  }, [id, buildUpdatedMatchDocumentJson, navigate, registerEdits])

  // Force-create after reviewing warnings
  const handleConfirmCreate = useCallback(async () => {
    if (!id || creationLocked.current) return
    if (!validationResult?.isValid || validatedRevision.current !== matchDocRevision.current) {
      setValidationResult(null)
      setError("Decisions changed after validation. Save and validate them before confirming creation.")
      return
    }
    creationLocked.current = true
    setError(null)
    setCreating(true)
    try {
      const result = await api.post<CreateBenefitResult>(`/api/pse-online/records/${id}/create-benefit`)
      if (result.success && result.benefitId) {
        setCreationHandoffCompleted(true)
        registerEdits(false)
        navigate(pseBenefitResumePath(result.benefitId))
        return
      }
      setCreateResult(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create benefit")
    } finally {
      creationLocked.current = false
      setCreating(false)
    }
  }, [id, navigate, registerEdits, validationResult])

  const durableBenefitId = durablePseBenefitId(matchDocResponse)
  const openAnchoredApplication = useCallback(() => {
    if (durableBenefitId) navigate(pseBenefitResumePath(durableBenefitId))
  }, [durableBenefitId, navigate])

  // ── Loading State ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-6 w-20" />
        </div>
        <div className="grid gap-6">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    )
  }

  // ── Error State ───────────────────────────────────────────────────
  if (error && !record) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate("/pse-online")} className="mb-4">
          <IconArrowLeft className="mr-1 size-4" /> Back to PSE Online
        </Button>
        <Alert variant="destructive">
          <IconAlertTriangle className="size-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!record) return null

  const appData = record.applicationData
  const creationCapability = matchDocResponse?.creationCapability
  const capabilityLabel = creationCapability?.mode === "V2"
    ? "V2 SDH creation"
    : creationCapability?.mode === "Compatibility"
      ? "Compatibility creation"
      : creationCapability?.mode === "AlreadyCreated"
        ? "Already created"
        : "Creation rejected"

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6" inert={creating || applyingSnapshot} aria-busy={creating || applyingSnapshot}>
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" onClick={() => navigate("/pse-online")} className="-ml-2 mb-1">
            <IconArrowLeft className="mr-1 size-4" /> Back to PSE Online
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">
              PSE Application #{record.pseApplicationId}
            </h1>
            <Badge variant="secondary">PY {record.programYear}</Badge>
            <Badge variant="outline" title={record.pseStatus}>{record.pseStatusLabel ?? record.pseStatus}</Badge>
            {matchDoc && <ConfidenceBadge confidence={matchDoc.overallConfidence} />}
            {matchDocResponse && <DesignationStatusBadge status={matchDocResponse.status} />}
            <StaffProgressBadge status={record.staffProgressStatus} />
          </div>
          <p className="text-muted-foreground text-sm">
              PSE application date: {formatDate(record.applicationCreatedDate)}
              <span className="ml-2">(source-system date; CMS intake timestamps are separate)</span>
          </p>
          {creationCapability && (
            <Alert variant={!creationCapability.canCreate && creationCapability.mode !== "AlreadyCreated" ? "destructive" : "default"}>
              <IconClipboardCheck className="size-4" />
              <AlertDescription>
                <span className="font-medium">Server decision: {capabilityLabel}.</span>{" "}
                {creationCapability.mode === "AlreadyCreated" && durableBenefitId
                  ? <>
                      CMS benefit {durableBenefitId} is the durable result; no new root will be created.
                      <Button type="button" variant="link" size="sm" className="h-auto px-1 py-0 align-baseline" onClick={openAnchoredApplication}>
                        Open / Resume application <IconExternalLink className="ml-1 inline size-3" />
                      </Button>
                    </>
                  : creationCapability.disabledReason ?? "This mode is selected by the server; the browser cannot switch to another creation path."}
              </AlertDescription>
            </Alert>
          )}
          {record.accountHolderFirstName && (
            <p className="text-muted-foreground text-sm">
              {formatPersonName({ firstName: record.accountHolderFirstName, lastName: record.accountHolderLastName })}
              {record.accountNumber && <> &middot; Account {record.accountNumber}</>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {saveSuccess && (
            <span className="flex items-center gap-1 text-sm text-green-600">
              <IconCheck className="size-4" /> Saved
            </span>
          )}
          {!matchDoc && !loading && (
            <Button variant="outline" onClick={handleReprocess} disabled={reprocessing || hasUnsavedDecisions}>
              {reprocessing ? <IconLoader2 className="mr-1 size-4 animate-spin" /> : <IconPlayerPlay className="mr-1 size-4" />}
              Run Matching
            </Button>
          )}
          {hasUnsavedDecisions && <Button variant="ghost" disabled={saving || creating || validating} onClick={() => {
            matchDocRevision.current += 1
            validatedRevision.current = null
            setMatchDoc(JSON.parse(savedDocument))
            setValidationResult(null)
            setCreateResult(null)
          }}>Discard unsaved decisions</Button>}
          <Button variant="outline" onClick={handleSave} disabled={saving || validating || creating || !matchDoc}>
            {saving ? <IconLoader2 className="mr-1 size-4 animate-spin" /> : <IconDeviceFloppy className="mr-1 size-4" />}
            Save Progress
          </Button>
          <Button onClick={handleCreateBenefit} disabled={saving || validating || creating || !matchDoc || !!createResult?.success || creationCapability?.canCreate === false}>
            {(validating || creating) ? (
              <IconLoader2 className="mr-1 size-4 animate-spin" />
            ) : (
              <IconCirclePlus className="mr-1 size-4" />
            )}
            {creationCapability?.mode === "V2" ? "Create V2 SDH Application" : "Create Benefit Application"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Assigned intake staff</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">
              {record.assignedStaffDisplayName ?? record.assignedStaffId ?? "Unassigned"}
            </span>
            {canManageDistribution && matchDocResponse && !matchDocResponse.createdBenefitId && (
              <Select
                value={distributionStaff.some((staff) => staff.id === record.assignedStaffId) ? assignmentStaffId : "unassigned"}
                onValueChange={(value) => void handleAssignmentChange(value)}
                disabled={assignmentSaving}
              >
                <SelectTrigger className="w-[230px]" aria-label="Assigned intake staff">
                  <SelectValue placeholder="Assign intake staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {distributionStaff.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>{staff.displayName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {assignmentSaving && <IconLoader2 className="size-4 animate-spin" />}
            {assignmentMessage && <span className="text-sm text-muted-foreground">{assignmentMessage}</span>}
          </div>
          <p className="text-xs text-muted-foreground">
            Assignment version {record.assignmentVersion}. This queue owner is not ownership of an already-created CMS benefit file.
          </p>
          {assignmentHistory.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer font-medium">Assignment history ({assignmentHistory.length})</summary>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                {assignmentHistory.map((entry) => (
                  <li key={entry.id}>
                    {entry.oldAssignedStaffId ?? "Unassigned"} → {entry.newAssignedStaffId ?? "Unassigned"}
                    {" · "}{entry.actorId} · {new Date(entry.changedAt).toLocaleString()} · version {entry.newVersion}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Staff progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={staffProgressStatus}
              onValueChange={(value) => {
                setStaffProgressStatus(value)
                void handleStaffProgressChange(value)
              }}
              disabled={!canUpdateStaffProgress || staffProgressSaving}
            >
              <SelectTrigger className="w-[180px]" aria-label="Staff progress status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Not Started</SelectItem>
                <SelectItem value="1">In Progress</SelectItem>
                <SelectItem value="2">Processed</SelectItem>
              </SelectContent>
            </Select>
            {staffProgressSaving && <IconLoader2 className="size-4 animate-spin" />}
            {staffProgressMessage && <span className="text-sm text-muted-foreground">{staffProgressMessage}</span>}
          </div>
          <p className="text-xs text-muted-foreground">
            Staff progress is separate from source PSE status and background processing. Version {record.staffProgressVersion}
            {record.staffProgressChangedBy && <> · Last changed by {record.staffProgressChangedBy}</>}
            {record.staffProgressChangedAt && <> on {new Date(record.staffProgressChangedAt).toLocaleString()}</>}
          </p>
          {staffProgressHistory.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer font-medium">Progress history ({staffProgressHistory.length})</summary>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                {staffProgressHistory.map((entry) => (
                  <li key={entry.id}>
                    {staffProgressStatusLabels[staffProgressStatusValue(entry.fromStatus)] ?? String(entry.fromStatus)} → {staffProgressStatusLabels[staffProgressStatusValue(entry.toStatus)] ?? String(entry.toStatus)}
                    {" · "}{entry.changedBy} · {new Date(entry.changedAt).toLocaleString()}
                    {entry.notes && <> · {entry.notes}</>}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </CardContent>
      </Card>

      {/* ── Inline Error Banner ─────────────────────────────────── */}
      {error && (
        <Alert variant="destructive">
          <IconAlertTriangle className="size-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* ── Validation Results ──────────────────────────────────── */}
      {reprocessMessage && (
        <p className="text-sm text-muted-foreground">{reprocessMessage}</p>
      )}

      {validationResult && !validationResult.isValid && (
        <Alert variant="destructive">
          <IconAlertTriangle className="size-4" />
          <AlertDescription>
            <p className="mb-2 font-medium">Cannot create benefit application:</p>
            <ul className="list-inside list-disc space-y-1 text-sm">
              {validationResult.errors.map((e, i) => (
                <li key={i}>{e.field}: {e.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {validationResult && validationResult.isValid && validationResult.warnings.length > 0 && !createResult?.success && creationCapability?.canCreate !== false && (
        <Alert>
          <IconAlertTriangle className="size-4" />
          <AlertDescription>
            <p className="mb-2 font-medium">Potential duplicate evidence — supervisor review is required:</p>
            <ul className="list-inside list-disc space-y-1 text-sm">
              {validationResult.warnings.map((w, i) => (
                <li key={i}>
                  {w.field}: {w.message}
                  {w.confidence !== undefined && <> (confidence: {Math.round(w.confidence * 100)}%)</>}
                </li>
              ))}
            </ul>
            <Button size="sm" className="mt-3" onClick={handleConfirmCreate} disabled={creating}>
              {creating ? <IconLoader2 className="mr-1 size-4 animate-spin" /> : <IconCheck className="mr-1 size-4" />}
              Submit for supervisor review
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* ── Create Result ───────────────────────────────────────── */}
      {createResult && (
        <Alert variant={createResult.success ? "default" : "destructive"}>
          {createResult.success ? <IconCheck className="size-4" /> : <IconAlertTriangle className="size-4" />}
          <AlertDescription>
            <p className="font-medium">{createResult.message}</p>
            {createResult.benefitId && (
              <p className="mt-1 text-sm">
                Benefit ID:{" "}
                <button
                  type="button"
                  onClick={() => navigate(`/benefit-applications/${createResult.benefitId}`)}
                  className="font-mono text-primary underline underline-offset-2"
                >
                  {createResult.benefitId}
                </button>
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* ── PSE Companion Snapshot & Dispatch ─────────────────── */}
      <Card data-testid="pse-companion-card">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle className="text-lg">PSE Companion Snapshot</CardTitle>
              <p className="text-muted-foreground text-sm">
                Enrich this application with full household dates of birth, SSN last four, and income details from PSE.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {companionStatus?.ready ? (
                <Badge className="bg-green-600 text-white">Companion Ready</Badge>
              ) : companionStatus?.connected ? (
                <Badge variant="secondary">
                  Companion Connected ({!companionStatus.signedInToPse ? "Sign in to PSE" : !companionStatus.allowDispatch ? "Dispatch off" : "Busy"})
                </Badge>
              ) : (
                <Badge variant="outline">Companion Offline</Badge>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="dispatch-companion-export"
                disabled={dispatchingCompanion || !companionStatus?.ready || !!matchDocResponse?.createdBenefitId}
                onClick={() => void handleDispatchCompanion()}
              >
                {dispatchingCompanion ? <IconLoader2 className="mr-1 size-4 animate-spin" /> : <IconRefresh className="mr-1 size-4" />}
                {snapshotReview ? "Re-export from PSE Companion" : "Export from PSE Companion"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {companionMessage && <p className="text-sm text-muted-foreground" role="status">{companionMessage}</p>}
          {!snapshotReview ? (
            <p className="text-sm text-muted-foreground">
              No snapshot has been imported for application #{record.pseApplicationId} (PY {record.programYear}) yet.
              {companionStatus?.ready
                ? " Click Export from PSE Companion above to capture it automatically."
                : " Open the PSE Companion desktop app, sign in to PSE, or import a .psejson file."}
            </p>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/20 p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">
                    Snapshot captured {new Date(snapshotReview.summary.capturedAt).toLocaleString()} · {snapshotReview.summary.householdMemberCount} household member(s) · {snapshotReview.summary.incomeRecordCount} income record(s)
                  </span>
                  {matchDoc?.snapshotEnrichment?.snapshotId === snapshotReview.receipt.snapshotId && (
                    <Badge className="bg-blue-600 text-white">Confirmed & Applied</Badge>
                  )}
                </div>
                {incompleteNotice(snapshotReview.summary) && (
                  <p className="mt-1 text-amber-700 dark:text-amber-400">{incompleteNotice(snapshotReview.summary)}</p>
                )}
              </div>

              <div className="space-y-3">
                {snapshotReview.household.map((member, index) => {
                  const decisionState = snapshotDecisions[index] ?? {
                    memberIndex: index,
                    decision: "no" as const,
                    selectedPersonId: member.candidates[0]?.personId ?? null,
                  }
                  const includeMember = decisionState.decision !== "no"
                  return (
                    <div key={index} className="grid gap-4 rounded-lg border p-4 md:grid-cols-2" data-testid={`snapshot-member-${index}`}>
                      <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Online (PSE Snapshot)</p>
                        <p className="font-medium">
                          {snapshotFullName(member)}
                          {member.isApplicant ? " · PSE Applicant (not necessarily the utility account holder)" : " · Household Member"}
                          {member.isDisabled ? " · Disabled" : ""}
                        </p>
                        <p className="text-sm font-medium">Destination: {snapshotDestination(member, appData)}</p>
                        <p className="text-sm text-muted-foreground">
                          {member.dateOfBirth ? `DOB: ${formatDate(member.dateOfBirth)}` : "DOB: Not recorded"}
                          {member.ssnLast4 ? ` · SSN last 4: •••-••-${member.ssnLast4}` : ""}
                        </p>
                      </div>

                      <div className="space-y-2 rounded-md border p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">CMS (Person Match Decision)</p>
                        {member.candidates.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No match found.</p>
                        ) : (
                          <div className="space-y-2">
                            {member.candidates.map((candidate) => (
                              <div key={candidate.personId} className="rounded border bg-background p-2 text-sm">
                                <div className="flex flex-wrap items-center gap-2">
                                  <ConfidenceBadge confidence={candidate.confidenceScore} />
                                  <span className="font-medium">{candidate.firstName} {candidate.lastName}</span>
                                  {candidate.dateOfBirth && <span className="text-muted-foreground text-xs">DOB: {formatDate(candidate.dateOfBirth)}</span>}
                                  {snapshotReview.authorizedIdentifiers?.[candidate.personId] ? (
                                    <span className="text-muted-foreground text-xs">SSN: {snapshotReview.authorizedIdentifiers[candidate.personId]}</span>
                                  ) : candidate.ssnLast4 ? (
                                    <span className="text-muted-foreground text-xs">SSN last 4: •••-••-{candidate.ssnLast4}</span>
                                  ) : null}
                                </div>
                                {candidate.previousApplications && candidate.previousApplications.length > 0 && (
                                  <div className="mt-1.5 space-y-0.5 border-t pt-1 text-xs">
                                    {candidate.previousApplications.map((app) => (
                                      <button
                                        key={app.benefitId}
                                        type="button"
                                        className="block text-left text-primary underline-offset-2 hover:underline"
                                        onClick={() => setBenefitPreviewId(app.benefitId)}
                                      >
                                        {app.fileNumber > 0 ? `File #${app.fileNumber}` : "File number unavailable"} · PY {app.programYear}{formatPseApplicationType(app.pseApplicationType) ? ` · ${formatPseApplicationType(app.pseApplicationType)}` : ""} · {app.address}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3 md:col-span-2">
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-medium">Apply this snapshot person to PSE Online record?</span>
                          <RadioGroup
                            value={includeMember ? "yes" : "no"}
                            onValueChange={(val) => {
                              setSnapshotDecisions((prev) => ({
                                ...prev,
                                [index]: {
                                  memberIndex: index,
                                  decision: val === "no"
                                    ? "no"
                                    : (member.candidates.length > 0 ? "use_existing" : "create_new"),
                                  selectedPersonId: val === "no" ? null : (decisionState.selectedPersonId ?? member.candidates[0]?.personId ?? null),
                                },
                              }))
                            }}
                            className="flex items-center gap-4"
                          >
                            <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                              <RadioGroupItem value="no" id={`snap-inc-no-${index}`} />
                              <span>No</span>
                            </label>
                            <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                              <RadioGroupItem value="yes" id={`snap-inc-yes-${index}`} />
                              <span>Yes</span>
                            </label>
                          </RadioGroup>
                        </div>

                        {includeMember && (
                          <div className="flex flex-wrap items-center gap-2">
                            <Select
                              value={decisionState.decision === "use_existing" && decisionState.selectedPersonId ? `existing:${decisionState.selectedPersonId}` : "create_new"}
                              onValueChange={(val) => {
                                if (val === "create_new") {
                                  setSnapshotDecisions((prev) => ({
                                    ...prev,
                                    [index]: { memberIndex: index, decision: "create_new", selectedPersonId: null },
                                  }))
                                } else if (val.startsWith("existing:")) {
                                  setSnapshotDecisions((prev) => ({
                                    ...prev,
                                    [index]: { memberIndex: index, decision: "use_existing", selectedPersonId: val.replace("existing:", "") },
                                  }))
                                }
                              }}
                            >
                              <SelectTrigger className="w-[280px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {member.candidates.map((c) => (
                                  <SelectItem key={c.personId} value={`existing:${c.personId}`}>
                                    Use CMS Person: {c.firstName} {c.lastName} ({Math.round(c.confidenceScore * 100)}%)
                                  </SelectItem>
                                ))}
                                <SelectItem value="create_new">Create New Person at File Creation</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              {!matchDocResponse?.createdBenefitId && (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {hasUnsavedDecisions && <p className="text-sm text-amber-700">Save or discard your matching edits before confirming a snapshot.</p>}
                  <Button
                    type="button"
                    data-testid="confirm-snapshot-decisions"
                    disabled={applyingSnapshot || hasUnsavedDecisions || Object.values(snapshotDecisions).every((d) => d.decision === "no")}
                    onClick={() => void handleApplySnapshotDecisions()}
                  >
                    {applyingSnapshot ? <IconLoader2 className="mr-1 size-4 animate-spin" /> : <IconCheck className="mr-1 size-4" />}
                    Confirm Snapshot Decisions
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Section 1: Applicant (Account Holder) ──────────────── */}
      {matchDoc && !matchDoc.accountHolderHouseholdExclusionReason && (
        <PersonMatchSelector
          title="Account Holder"
          icon={<IconUser className="size-5" />}
          csvData={matchDoc.accountHolder.csvData}
          residentialAddress={formatAddress(appData?.serviceAddress)}
          matches={matchDoc.accountHolder.matches}
          decision={matchDoc.accountHolder.decision}
          selectedPersonId={matchDoc.accountHolder.selectedPersonId}
          onDecisionChange={(decision, personId) => updatePersonDecision("accountHolder", decision, personId)}
          onViewBenefit={(benefitId) => setBenefitPreviewId(benefitId)}
          newPersonData={matchDoc.accountHolder.newPersonData}
          onNewPersonDataChange={(data) => updateNewPersonData("accountHolder", data)}
          onRefreshMatches={(data) => refreshPersonMatches("accountHolder", data)}
          authorizedIdentifiers={matchDocResponse?.authorizedIdentifiers}
          canEnterFullIdentifiers={matchDocResponse?.canEnterFullIdentifiers}
          excludedPersonIds={[
            matchDoc.coCustomer?.selectedPersonId ?? null,
            ...matchDoc.householdMembers.filter(m => m.decision === "use_existing").map(m => m.selectedPersonId),
          ].filter((id): id is string => Boolean(id))}
          onSelectExistingPerson={(match) => selectSectionExistingPerson("accountHolder", match)}
        />
      )}

      {matchDoc && <Card>
        <CardHeader><CardTitle className="text-base">Utility account holder and household inclusion</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm">Imported utility holder: {appData?.accountHolder.firstName} {appData?.accountHolder.lastName}. Snapshot applicant and CMS applicant are separate roles.</p>
          {matchDoc.accountHolderHouseholdExclusionReason ? <>
            <p className="text-sm">Bill identity only, not included in the CMS household. Reason: {matchDoc.accountHolderHouseholdExclusionReason}</p>
            <Button variant="outline" disabled={!!durableBenefitId} onClick={() => {
              if (creationLocked.current) return
              matchDocRevision.current += 1
              setMatchDoc({ ...matchDoc, accountHolderHouseholdExclusionReason: null,
                applicantSelection: { selectedPersonId: null, selectedPersonSlot: null, accountHolderRelationship: null, invalidated: true, relationshipReviewRequired: true } })
              setValidationResult(null)
            }}>Restore holder to household</Button>
          </> : <div className="space-y-2">
            <Label htmlFor="bill-holder-exclusion">If this utility holder is not a household member, enter the reason</Label>
            <Input id="bill-holder-exclusion" value={billHolderExclusionReason} maxLength={500} onChange={event => setBillHolderExclusionReason(event.target.value)} disabled={!!durableBenefitId} />
            <Button variant="outline" disabled={!!durableBenefitId || !billHolderExclusionReason.trim()} onClick={() => {
              if (creationLocked.current) return
              matchDocRevision.current += 1
              setMatchDoc({ ...matchDoc, accountHolderHouseholdExclusionReason: billHolderExclusionReason.trim(),
                applicantSelection: { selectedPersonId: null, selectedPersonSlot: null, accountHolderRelationship: null, invalidated: true, relationshipReviewRequired: true } })
              setValidationResult(null)
            }}>Confirm bill identity only, exclude from household</Button>
            <p className="text-xs text-muted-foreground">Explicit staff decision only. This preserves the bill name, creates no CMS person for the holder, and requires choosing an included applicant and reviewing relationships.</p>
          </div>}
        </CardContent>
      </Card>}

      {/* ── Section 2: Co-Applicant ────────────────────────────── */}
      {matchDoc?.coCustomer && (
        <PersonMatchSelector
          title="Co-Applicant (Co-Customer)"
          icon={<IconUsers className="size-5" />}
          csvData={matchDoc.coCustomer.csvData}
          matches={matchDoc.coCustomer.matches}
          decision={matchDoc.coCustomer.decision}
          selectedPersonId={matchDoc.coCustomer.selectedPersonId}
          onDecisionChange={(decision, personId) => updatePersonDecision("coCustomer", decision, personId)}
          onViewBenefit={(benefitId) => setBenefitPreviewId(benefitId)}
          newPersonData={matchDoc.coCustomer.newPersonData}
          onNewPersonDataChange={(data) => updateNewPersonData("coCustomer", data)}
          onRefreshMatches={(data) => refreshPersonMatches("coCustomer", data)}
          authorizedIdentifiers={matchDocResponse?.authorizedIdentifiers}
          canEnterFullIdentifiers={matchDocResponse?.canEnterFullIdentifiers}
          excludedPersonIds={[
            matchDoc.accountHolder.selectedPersonId,
            ...matchDoc.householdMembers.filter(m => m.decision === "use_existing").map(m => m.selectedPersonId),
          ].filter((id): id is string => Boolean(id))}
          onSelectExistingPerson={(match) => selectSectionExistingPerson("coCustomer", match)}
        />
      )}

      {/* ── Section 3: Household Members ───────────────────────── */}
      {matchDoc && appData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <IconUsers className="size-5" />
              Household review
            </CardTitle>
            <p className="text-muted-foreground text-sm">
              Original source count: <span className="font-medium text-foreground">{appData.household.householdMembersCount}</span>.
              Reviewed/effective count: <span className="font-medium text-foreground">{reviewedHouseholdSummary(matchDoc).count}</span>
              {reviewedHouseholdSummary(matchDoc).pending > 0 && ` (${reviewedHouseholdSummary(matchDoc).pending} decision${reviewedHouseholdSummary(matchDoc).pending === 1 ? "" : "s"} pending)`}.
              Source age buckets remain available below as provenance; the created CMS file uses the included canonical people.
            </p>
          </CardHeader>
          {matchDoc.householdMembers.length > 0 && (
            <CardContent className="space-y-4">
              {matchDoc.householdMembers.map((member) => {
                const currentValue = member.decision === "use_existing" && member.selectedPersonId
                  ? `existing_${member.selectedPersonId}`
                  : member.decision === "create_new"
                    ? "create_new"
                    : member.decision === "exclude"
                      ? "exclude"
                      : ""
                const selectedHouseholdIds = [
                  matchDoc.accountHolder.selectedPersonId,
                  matchDoc.coCustomer?.selectedPersonId ?? null,
                  ...matchDoc.householdMembers
                    .filter(candidate => candidate.index !== member.index && candidate.decision === "use_existing")
                    .map(candidate => candidate.selectedPersonId),
                ].filter((personId): personId is string => Boolean(personId))

                return (
                  <Card key={member.index} className={cn("border-dashed", member.decision === "exclude" && "opacity-80")}>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <IconUser className="size-4" />
                        Additional Member #{member.index + 1}
                        {member.snapshotMember?.name && (
                          <span className="font-normal text-muted-foreground">({member.snapshotMember.name})</span>
                        )}
                        <Badge variant={member.decision === "exclude" ? "destructive" : "secondary"}>
                          {member.decision === "exclude" ? "Excluded" : `Age: ${member.ageRange}`}
                        </Badge>
                        {member.snapshotMember && <Badge variant="outline">From Snapshot</Badge>}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {member.decision !== "exclude" && (
                        <ExistingPersonSearch
                          selectedPersonId={member.selectedPersonId}
                          excludedPersonIds={selectedHouseholdIds}
                          canSearch={matchDocResponse?.canEnterFullIdentifiers === true}
                          idPrefix={`hh-${member.index}`}
                          onSelect={(match) => selectHouseholdExistingPerson(member.index, match)}
                        />
                      )}
                      <RadioGroup
                        value={currentValue}
                        onValueChange={(val) => {
                          if (val === "create_new") {
                            updateHouseholdDecision(member.index, "create_new", null)
                          } else if (val === "exclude") {
                            updateHouseholdDecision(member.index, "exclude", null, member.exclusionReason ?? "")
                          } else if (val.startsWith("existing_")) {
                            updateHouseholdDecision(member.index, "use_existing", val.replace("existing_", ""))
                          }
                        }}
                        className="gap-3"
                      >
                        {member.matches.map((match) => (
                          <label
                            key={match.personId}
                            htmlFor={`hh-${member.index}-${match.personId}`}
                            className={cn(
                              "flex cursor-pointer flex-col gap-2 rounded-lg border p-3 transition-colors hover:bg-accent/50",
                              currentValue === `existing_${match.personId}` && "border-primary bg-accent/40",
                            )}
                          >
                            <div className="flex items-start gap-3">
                              <RadioGroupItem value={`existing_${match.personId}`} id={`hh-${member.index}-${match.personId}`} className="mt-0.5" />
                              <div className="flex-1 space-y-1">
                                <div className="flex items-center gap-2">
                                  <ConfidenceBadge confidence={match.confidence} />
                                  <span className="font-medium">
                                    {match.firstName && match.lastName
                                      ? formatPersonName(match)
                                      : match.matchType}
                                  </span>
                                  {match.dateOfBirth && (
                                    <span className="text-muted-foreground text-sm">DOB: {formatDate(match.dateOfBirth)}</span>
                                  )}
                                  {matchDocResponse?.authorizedIdentifiers?.[match.personId] ? (
                                    <span className="text-muted-foreground text-sm">SSN: {matchDocResponse.authorizedIdentifiers[match.personId]}</span>
                                  ) : match.ssnLast4 ? (
                                    <span className="text-muted-foreground text-sm">SSN last 4: •••-••-{match.ssnLast4}</span>
                                  ) : null}
                                </div>
                                <p className="text-muted-foreground text-xs">
                                  {match.matchType} · Matched on: {match.matchedFields.join(", ")}
                                </p>
                              </div>
                            </div>
                            {match.previousApplications && match.previousApplications.length > 0 && (
                              <div className="ml-8 rounded-md bg-muted/30 p-2">
                                <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wide">
                                  Previous Applications ({match.previousApplications.length})
                                </p>
                                <div className="space-y-0.5">
                                  {match.previousApplications.map((app) => (
                                    <button
                                      key={app.benefitId}
                                      type="button"
                                      className="block text-left text-xs text-primary underline-offset-2 hover:underline"
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBenefitPreviewId(app.benefitId) }}
                                    >
                                      {app.fileNumber > 0 ? `File #${app.fileNumber}` : "File number unavailable"} · PY {app.programYear}{formatPseApplicationType(app.pseApplicationType) ? ` · ${formatPseApplicationType(app.pseApplicationType)}` : ""} · {app.address}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </label>
                        ))}
                        <div
                          className={cn(
                            "rounded-lg border border-dashed p-3 transition-colors hover:bg-accent/50",
                            currentValue === "create_new" && "border-primary bg-accent/40",
                          )}
                        >
                          <label htmlFor={`hh-${member.index}-create-new`} className="flex cursor-pointer items-center gap-3">
                            <RadioGroupItem value="create_new" id={`hh-${member.index}-create-new`} />
                            <span className="flex items-center gap-2">
                              <IconCirclePlus className="size-4 text-muted-foreground" />
                              <span className="text-sm font-medium">Create New Person</span>
                            </span>
                          </label>
                          {member.decision === "create_new" && (
                            <NewPersonForm
                              data={member.newPersonData ?? emptyNewPersonData()}
                              onChange={(data) => updateHouseholdNewPersonData(member.index, data)}
                              idPrefix={`hh-${member.index}`}
                              canEnterFullIdentifiers={matchDocResponse?.canEnterFullIdentifiers === true}
                            />
                          )}
                        </div>
                        <div
                          className={cn(
                            "rounded-lg border border-dashed p-3 transition-colors hover:bg-accent/50",
                            currentValue === "exclude" && "border-destructive bg-destructive/5",
                          )}
                        >
                          <label htmlFor={`hh-${member.index}-exclude`} className="flex cursor-pointer items-center gap-3">
                            <RadioGroupItem value="exclude" id={`hh-${member.index}-exclude`} />
                            <span className="text-sm font-medium">Exclude erroneous row</span>
                          </label>
                          {member.decision === "exclude" && (
                            <div className="mt-3 space-y-2" onClick={(event) => event.stopPropagation()}>
                              <Label htmlFor={`hh-${member.index}-exclusion-reason`}>Reason (required)</Label>
                              <Input
                                id={`hh-${member.index}-exclusion-reason`}
                                value={member.exclusionReason ?? ""}
                                onChange={(event) => updateHouseholdExclusionReason(member.index, event.target.value)}
                                placeholder="Explain why this source row is not part of the household"
                                maxLength={500}
                              />
                              <p className="text-muted-foreground text-xs">The original source row remains preserved. No CMS person or snapshot will be created.</p>
                              <Button type="button" size="sm" variant="outline" onClick={() => restoreHouseholdMember(member.index)}>
                                Restore row
                              </Button>
                            </div>
                          )}
                        </div>
                      </RadioGroup>
                    </CardContent>
                  </Card>
                )
              })}
            </CardContent>
          )}
        </Card>
      )}

      {/* ── Section 3a: CMS Applicant Selection ─────────────────── */}
      {durableBenefitId && <SourceCurrentApplication benefitId={durableBenefitId} />}
      {matchDoc && (durableBenefitId
        ? <details><summary>Original applicant selection (source-at-creation evidence)</summary><div inert><ApplicantSelectionSection matchDoc={matchDoc} onChange={updateApplicantSelection} /></div></details>
        : <ApplicantSelectionSection matchDoc={matchDoc} onChange={updateApplicantSelection} />)}

      {/* ── Section 3b: Income Assignment ──────────────────────── */}
      {matchDoc && appData && (
        <IncomeAssignmentSection
          matchDoc={matchDoc}
          hasCoApplicant={matchDoc.coCustomer !== null}
          onChange={updateIncomeAssignment}
        />
      )}

      {/* ── Section 4: Application Data Comparison ──────────────── */}
      {appData && (
        <ApplicationDataComparison
          appData={appData}
          matchDoc={matchDoc}
          authorizedIdentifiers={matchDocResponse?.authorizedIdentifiers}
        />
      )}

      {/* ── Section 6: Detailed Application Data ───────────────── */}
      {appData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <IconClipboardCheck className="size-5" />
              Detailed Application Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <DataSection title="Service Address" icon={<IconHome className="size-4" />} defaultOpen>
              <DataRow label="Address" value={formatAddress(appData.serviceAddress)} />
            </DataSection>

            <Separator />

            <DataSection title="Mailing Address" icon={<IconMail className="size-4" />}>
              <DataRow label="Address" value={formatAddress(appData.mailingAddress)} />
            </DataSection>

            <Separator />

            <DataSection title="Energy Info" icon={<IconFlame className="size-4" />}>
              <DataRow label="Energy Type" value={formatEnergyType(appData.energyInfo.energyType)} />
              <DataRow label="Annual Electric" value={formatCurrency(appData.energyInfo.annualElectricAmount)} />
              <DataRow label="Annual Gas" value={formatCurrency(appData.energyInfo.annualGasAmount)} />
              {Object.entries(appData.energyInfo)
                .filter(([k]) => !["energyType", "annualElectricAmount", "annualGasAmount"].includes(k))
                .map(([k, v]) => (
                  <DataRow key={k} label={k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())} value={String(v)} />
                ))}
            </DataSection>

            <Separator />

            <DataSection title="Eligibility" icon={<IconClipboardCheck className="size-4" />}>
              <DataRow label="PSE Help Eligible" value={appData.eligibility.isPseHelpEligible ? "Yes" : "No"} />
              <DataRow label="Eligible Amount" value={formatCurrency(appData.eligibility.eligibilityAmount)} />
              <DataRow label="Category" value={appData.eligibility.eligibilityCategory} />
              {Object.entries(appData.eligibility)
                .filter(([k]) => !["isPseHelpEligible", "eligibilityAmount", "eligibilityCategory"].includes(k))
                .map(([k, v]) => (
                  <DataRow key={k} label={k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())} value={String(v)} />
                ))}
            </DataSection>

            <Separator />

            <DataSection title="Payment" icon={<IconCurrencyDollar className="size-4" />}>
              <DataRow label="Total Paid" value={formatCurrency(appData.payment.totalPaidAmount)} />
              {appData.payment.vendorPayments.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-muted-foreground text-xs font-medium uppercase">Vendor Payments</p>
                  {appData.payment.vendorPayments.map((vp, i) => (
                    <div key={i} className="text-sm">
                      {Object.entries(vp).map(([k, v]) => (
                        <DataRow key={k} label={k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())} value={String(v)} />
                      ))}
                      {i < appData.payment.vendorPayments.length - 1 && <Separator className="my-1" />}
                    </div>
                  ))}
                </div>
              )}
            </DataSection>

            <Separator />

            <DataSection title="Household" icon={<IconBuildingCommunity className="size-4" />}>
              <DataRow label="Members" value={appData.household.householdMembersCount} />
              <DataRow label="Monthly Income" value={formatCurrency(appData.household.monthlyIncome)} />
              <DataRow label="Adjusted Gross Income" value={formatCurrency(appData.household.adjustedGrossIncome)} />
              <Separator className="my-1" />
              <DataRow label="Ages 0–2" value={appData.household.numberOfPersonsAge0to2} />
              <DataRow label="Ages 3–5" value={appData.household.numberOfPersonsAge3to5} />
              <DataRow label="Ages 6–17" value={appData.household.numberOfPersonsAge6to17} />
              <DataRow label="Ages 18–59" value={appData.household.numberOfPersonsAge18to59} />
              <DataRow label="Ages 60+" value={appData.household.numberOfPersonsAge60Plus} />
              <DataRow label="Disabled" value={appData.household.numberOfPersonsDisabled} />
              <Separator className="my-1" />
              <DataRow label="Receiving Food Stamps" value={appData.household.receivingFoodStamps ? "Yes" : "No"} />
              <DataRow label="Military Member" value={appData.household.hasMilitaryMember ? "Yes" : "No"} />
              <DataRow label="Received Weatherization" value={appData.household.receivedWeatherization ? "Yes" : "No"} />
            </DataSection>

            <Separator />

            <DataSection title="Housing" icon={<IconHome className="size-4" />}>
              <DataRow label="Status" value={appData.housing.housingStatus} />
              <DataRow label="Type" value={appData.housing.housingType} />
            </DataSection>

            <Separator />

            <DataSection title="Contact" icon={<IconPhone className="size-4" />}>
              <DataRow label="Email" value={appData.contact.emailAddress} />
              <DataRow label="Phone" value={appData.contact.phoneNumber} />
              {Object.entries(appData.contact)
                .filter(([k]) => !["emailAddress", "phoneNumber"].includes(k))
                .map(([k, v]) => (
                  <DataRow key={k} label={k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())} value={String(v)} />
                ))}
            </DataSection>

            <Separator />

            <DataSection title="Income Sources" icon={<IconReceipt className="size-4" />}>
              <div className="flex flex-wrap gap-2 py-1">
                {Object.entries(appData.incomeSources)
                  .filter(([, v]) => v === true)
                  .map(([k]) => (
                    <Badge key={k} variant="secondary">
                      {k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())}
                    </Badge>
                  ))}
                {Object.values(appData.incomeSources).every((v) => !v) && (
                  <span className="text-muted-foreground text-sm">None reported</span>
                )}
              </div>
            </DataSection>
          </CardContent>
        </Card>
      )}

      {/* ── No Application Data Fallback ────────────────────────── */}
      {!appData && (
        <Alert>
          <AlertDescription>Application data has not been parsed for this record.</AlertDescription>
        </Alert>
      )}

      {/* ── No Match Document Fallback ──────────────────────────── */}
      {!matchDoc && !loading && (
        <Alert>
          <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>
              No match document available for this record. Run matching to screen this applicant
              against existing people and previous applications.
            </span>
            <Button size="sm" onClick={handleReprocess} disabled={reprocessing || hasUnsavedDecisions} className="shrink-0">
              {reprocessing ? <IconLoader2 className="mr-1 size-4 animate-spin" /> : <IconPlayerPlay className="mr-1 size-4" />}
              Run Matching
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* ── Benefit Preview Dialog ──────────────────────────────── */}
      <Dialog open={!!benefitPreviewId} onOpenChange={(open) => { if (!open) setBenefitPreviewId(null) }}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Benefit Application Summary</DialogTitle>
            <DialogDescription>Read-only preview of the selected existing application.</DialogDescription>
          </DialogHeader>
          {benefitPreviewLoading ? (
            <div className="space-y-3 py-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : benefitPreview ? (
            <div className="space-y-2 py-2">
              <DataRow label="File Number" value={benefitPreview.fileNumber > 0 ? benefitPreview.fileNumber : "Unavailable"} />
              <DataRow
                label="Program Year"
                value={benefitPreview.applicationDate
                  ? (applicableProgramYear(benefitPreview.applicationDate) ?? "Unavailable")
                  : "Unavailable"}
              />
              <DataRow
                label="Applicant"
                value={(() => {
                  const applicant = benefitPreview.personCollection?.find((person: { id?: string }) => person.id === benefitPreview.applicantId)
                  return applicant ? formatPersonName(applicant) : "Applicant unavailable"
                })()}
              />
              <DataRow
                label="Social Security Number"
                value={(() => {
                  const applicant = benefitPreview.personCollection?.find((person: { id?: string }) => person.id === benefitPreview.applicantId)
                  return getFullSsn(applicant?.socialSecurityNumber)
                })()}
              />
              <DataRow label="Application Date" value={formatDate(benefitPreview.applicationDate)} />
              <DataRow label="Certification Date" value={formatDate(benefitPreview.certificationDate)} />
              <DataRow label="Account Number(s)" value={getBenefitAccountNumbers(benefitPreview)} />
              <DataRow
                label="Residential Address"
                value={(() => {
                  const addr = benefitPreview.addresses?.["Residential"] ?? Object.values(benefitPreview.addresses ?? {})[0]
                  if (!addr || typeof addr !== "object") return "Not available"
                  const a = addr as { address?: string; city?: string; state?: string; zipCode?: string }
                  return [a.address, a.city, a.state, a.zipCode].filter(Boolean).join(", ") || "Not available"
                })()}
              />
              <DataRow label="Intake Date" value={formatDate(benefitPreview.intakeDate)} />
              <DataRow label="Household Size" value={benefitPreview.peopleInHousehold ?? "Not available"} />
            </div>
          ) : (
            <p className="text-muted-foreground py-4 text-sm">Failed to load benefit data.</p>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBenefitPreviewId(null)}>
              Close
            </Button>
            {benefitPreviewId && (
              <Button onClick={() => window.open(`/benefit-applications/${benefitPreviewId}`, "_blank")}>
                <IconExternalLink className="mr-1 size-4" />
                View Full Details
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
